import { useEffect, useMemo, useState, type ReactNode, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useTasks } from '../lib/tasks';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Globe,
  Link2,
  MonitorSmartphone,
  Network,
  Plus,
  RotateCw,
  Save,
  Trash2,
  Wifi,
} from 'lucide-react';
import {
  IPV6_PREFIX_MODE_LABELS,
  SUBNET_MASKS,
  findMirrorTarget,
  ipv6Refusals,
  wlanDeletable,
  mirrorStartsTicked,
  subnetLabel,
  WLAN_SECURITY_LABELS,
  wlanSecurityWrites,
} from '@simpleacs/shared';
import type {
  Capability,
  CliActionResult,
  DeviceDetail,
  Olt,
  OltVlan,
  WanCandidate,
  DeviceFault,
  DeviceTask,
  DeviceWarning,
  Ipv6PrefixMode,
  OnuPort,
  OnuVlan,
  Page,
  SubscriberWanWriter,
  Topology,
  VlanAddressing,
  WanIpv6,
  WanMode,
  WanOwner,
  Wlan,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { nextOpenKey } from '../lib/accordion';
import {
  IPV6_ROW_LABELS,
  defaultIpv6Rows,
  ipv6IntentFromRows,
  ipv6IsOn,
  ipv6RowRefusals,
  ipv6SwitchOffReason,
  ipv6SwitchOnPair,
  ipv6TitlePill,
  observedIpv6Data,
  observedIpv6Rows,
  recordedIpv6Label,
  subscriberWanRow,
  visibleIpv6Choices,
  wanIpv6QueryKey,
  type Ipv6RowChoice,
  type Ipv6TargetRead,
} from '../lib/wan-ipv6';
import { defaultFirewall, ipv6ChoiceMatrix } from '../lib/wan-wizard';
import {
  WAN_FIREWALL_DISCLOSURE,
  WAN_OWNER_LABELS,
  cardApplyDisclosure,
  cardPasswordToSend,
  firewallSourceLabel,
  firewallTitlePill,
  wanWriterProbeBody,
  type WanApplyChoice,
} from '../lib/wan-apply';
import { CollapsibleGroup, useDetailsItem, useUnsavedDraft } from '../components/ui/CollapsibleGroup';
import { FirewallPill, WanConnectionLine, WanDeleteButton, wanDeletable } from './device/WanConnections';
import { WanPorts } from './device/WanPorts';
import { wanPortTarget } from '../lib/wan-ports';
import { Ipv6RefusalFold } from './device/Ipv6RefusalFold';
import { TaskDetailDialog } from './device/TaskDetailDialog';
import { useWanApply } from './device/useWanApply';
import { formatTimestamp, formatRelative, formatUptime } from '../lib/format';
import { useSession } from '../lib/session';
import { useSyncedDraft } from '../lib/useSyncedDraft';
import { planWifiCardApply, type WifiNetworkChoice } from '../lib/wifi-card-apply';
import {
  managementApplyBlocked,
  managementApplyBody,
  managementApplyConfirm,
  managementDoneText,
  type ManagementCardChoice,
  type ManagementMode,
} from '../lib/management-card';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Input,
  Pill,
  SearchSelect,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/** One parameter write, as the API expects it. */
interface Write {
  path: string;
  value: string | number | boolean;
}

/**
 * Reset or delete an ONU on the OLT — the two destructive OLT-side actions on
 * the device page. Same honest flow the OLT console uses: fetch the exact
 * commands as a dry run, show them, make the operator type the serial to arm
 * the button, then run and show the transcript. GenieACS is never touched here;
 * these write to the chassis.
 */
export function OnuOltActionDialog({
  kind,
  olt,
  onu,
  onClose,
  href,
  invalidate,
}: {
  kind: 'recreate' | 'deauthorize' | 'factory-reset';
  // Only what the dialog actually names, so it can be opened from the OLT-side
  // inventory as well as from the ACS management context.
  olt: { id: string; name: string };
  onu: { id: string; serial: string; ponPort: string; onuIndex: string };
  onClose: () => void;
  /** Where the background toast/tray row links once the chassis has answered. */
  href?: string;
  /** Query keys to refresh once the delete/recreate lands. */
  invalidate?: QueryKey[];
}) {
  const tasks = useTasks();
  // Two deliberate presses rather than a typed serial. Typing the serial back
  // proves only that you can copy a string off the screen above it, which is
  // not the thing worth being sure of; pressing twice, with the consequence
  // stated in between, is.
  const [confirming, setConfirming] = useState(false);

  const path = `/olts/${encodeURIComponent(olt.id)}/onus/${encodeURIComponent(onu.id)}/${kind}`;
  const planQuery = useQuery({
    queryKey: ['onu-olt-action', onu.id, kind],
    queryFn: () => api.post<CliActionResult>(path, { dryRun: true }),
    staleTime: 0,
  });

  const meta =
    kind === 'factory-reset'
      ? {
          title: `Factory reset ${onu.serial}`,
          consequence:
            'Resets the router to factory settings. Its place on the OLT and its record stay; the platform puts its recorded WAN and WiFi back when it checks in.',
          confirm: 'Factory reset',
          verb: 'Factory reset',
          sure: `${onu.serial}'s router loses everything set on it and comes back as if new. The subscriber is offline until it has checked in again and the platform has put back the WAN and WiFi it records — usually a few minutes. Anything set on the router by hand and not recorded here (a port forward, an extra network) is gone.`,
        }
      : kind === 'deauthorize'
      ? {
          title: `Delete ${onu.serial}`,
          consequence:
            'Deleted here now; removed from the OLT when it answers — queued for up to 48 hours if it does not.',
          confirm: 'Delete',
          verb: 'Delete',
          sure: `Everything SimpleACS holds about ${onu.serial} goes now — its optical history, its events, its VLANs and ports — whether or not ${olt.name} answers. The chassis removal runs at once when the OLT is reachable; if it is not, it is queued and retried for 48 hours, and you are told on the bell if it never lands. The subscriber is offline until somebody authorises an ONU here again. Only the ONU type library is kept, because that describes the hardware rather than this subscriber.`,
        }
      : {
          title: `Recreate ${onu.serial}`,
          consequence:
            'Clears the ONU on the chassis and puts SimpleACS’s settings back on it — the subscriber drops briefly.',
          confirm: 'Recreate on the OLT',
          verb: 'Recreate',
          sure: `${onu.serial} loses whatever is currently on the chassis and comes back with the VLANs and ports SimpleACS holds for it, and its router is factory-reset on the way (its recorded WAN and WiFi are put back when it checks in). Anything set by hand on the OLT or the router and not recorded here is not restored. The subscriber drops while that happens.`,
        };

  /**
   * Apply CLOSES the dialog and hands the chassis write to the background tray
   * (like Resync and the WAN/management wizards) — deleting or recreating an ONT
   * over the CLI can take a while, and pinning the operator to a spinner on the
   * one destructive action was the wrong shape. The toast reports what the OLT
   * said; a failure stays until acknowledged.
   */
  const start = () => {
    const label = `${meta.verb} ${onu.serial}`;
    onClose();
    tasks.start<CliActionResult>({
      label,
      ...(href ? { href } : {}),
      run: () => api.post<CliActionResult>(path, { dryRun: false }),
      /**
       * A delete's warnings ARE the outcome — "deleted here, chassis removal
       * queued on gachie2 for 48 hours" — and after a delete there is no ONU
       * page to open. So the first warning is the toast; a recreate keeps
       * pointing at the page, which still exists.
       */
      done: (r) =>
        r.warnings.length === 0
          ? `${label} — the OLT accepted it.`
          : kind === 'deauthorize' || kind === 'factory-reset'
            ? `${label} — ${r.warnings[0]}`
            : `${label} — the OLT answered with warnings. Open ${onu.serial} to read them.`,
      invalidate: invalidate ?? [['onu', onu.id]],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">{meta.title}</h2>
          <p className="mt-0.5 text-xs text-muted">
            {olt.name} · {onu.ponPort}:{onu.onuIndex} — {meta.consequence}
          </p>
        </header>
        <div className="space-y-4 p-5">
          {planQuery.isLoading ? (
            <div className="flex justify-center p-6">
              <Spinner />
            </div>
          ) : planQuery.error ? (
            <ErrorNotice error={planQuery.error} />
          ) : (
            <>
              {/* What it does to the subscriber, not what it types at the
                  chassis. The commands are the platform's business. */}
              {(planQuery.data?.warnings ?? []).map((w) => (
                <div key={w} className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
                  {w}
                </div>
              ))}
              {confirming && (
                <div className="rounded-lg border border-danger/40 bg-danger-soft px-4 py-3">
                  <p className="text-sm font-medium text-danger">Are you sure?</p>
                  <p className="mt-1 text-sm text-danger/90">{meta.sure}</p>
                  <p className="mt-2 text-xs text-danger/80">
                    This closes and runs on the chassis in the background — watch the tray for what
                    the OLT says.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={planQuery.isLoading}
            onClick={() => (confirming ? start() : setConfirming(true))}
          >
            {confirming ? 'Confirm' : meta.confirm}
          </Button>
        </footer>
      </div>
    </div>
  );
}

// --- shared editing pieces ---------------------------------------------------

/**
 * Applies a set of parameter writes to the CPE. Nothing is sent for a field the
 * device does not expose — the API returns a null path for those and the input
 * is rendered read-only, so a change that would fail is never offered.
 */
function useApplyWrites(
  deviceId: string,
  onNotice: (message: string, tone?: 'info' | 'danger') => void,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Write[]) =>
      api.post<{ delivered: boolean; message: string }>(
        `/devices/${encodeURIComponent(deviceId)}/parameters`,
        { values, connectionRequest: true },
      ),
    onSuccess: async (result) => {
      onNotice(result.message);
      await queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not apply', 'danger'),
  });
}

/**
 * A subnet mask, chosen from the list rather than typed.
 *
 * There are about ten real masks and `255.255.255.0` differs from
 * `255.255.225.0` by one keystroke — a CPE accepts both and only one of them
 * routes. Each is labelled with its prefix length too, because an operator says
 * "a /24" and writes `255.255.255.0`.
 *
 * A value the CPE reports that is not a mask is still shown: something
 * unexpected on a subscriber's connection is worth seeing, not swallowing.
 */
function MaskRow({
  label,
  path,
  value,
  onChange,
  forceEditable = false,
}: {
  label: string;
  path: string | null | undefined;
  value: string;
  onChange: (next: string) => void;
  forceEditable?: boolean;
}) {
  const { can } = useSession();
  const editable = (forceEditable || Boolean(path)) && can('device.write');

  const options = SUBNET_MASKS.map((m) => ({ value: m.mask, label: m.label }));
  if (value && !options.some((o) => o.value === value)) {
    options.unshift({ value, label: subnetLabel(value) ?? value });
  }

  return (
    <div className="grid grid-cols-[9rem_1fr] items-center gap-3 px-5 py-2.5">
      <label className="text-sm text-muted" title={path ?? 'not exposed by this device'}>
        {label}
      </label>
      {editable ? (
        <SearchSelect value={value} options={options} onChange={onChange} placeholder="255.255.255.0 (/24)" />
      ) : (
        <span className="text-sm text-ink">
          {subnetLabel(value) ?? <span className="text-faint">—</span>}
          {!path && <span className="ml-2 text-xs text-faint">(not editable on this model)</span>}
        </span>
      )}
    </div>
  );
}

function EditRow({
  label,
  path,
  value,
  onChange,
  type = 'text',
  hint,
  forceEditable = false,
}: {
  label: string;
  /** Null or absent means the device does not expose it, so it renders read-only. */
  path: string | null | undefined;
  value: string;
  onChange: (next: string) => void;
  type?: 'text' | 'password' | 'number';
  hint?: string;
  /**
   * For values that feed a rebuild rather than a write to the current object —
   * the target object does not exist yet, so its paths cannot gate the input.
   */
  forceEditable?: boolean;
}) {
  const { can } = useSession();
  const editable = (forceEditable || Boolean(path)) && can('device.write');
  const [reveal, setReveal] = useState(false);

  return (
    <div className="grid grid-cols-[9rem_1fr] items-center gap-3 px-5 py-2.5">
      <label className="text-sm text-muted" title={path ?? 'not exposed by this device'}>
        {label}
      </label>
      {editable ? (
        <div className="flex gap-2">
          <Input
            type={type === 'password' && !reveal ? 'password' : type === 'number' ? 'number' : 'text'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={hint}
          />
          {type === 'password' && (
            <Button
              variant="secondary"
              icon={reveal ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              onClick={() => setReveal((v) => !v)}
            />
          )}
        </div>
      ) : (
        <span className="text-sm text-ink">
          {value || <span className="text-faint">—</span>}
          {!path && <span className="ml-2 text-xs text-faint">(not editable on this model)</span>}
        </span>
      )}
    </div>
  );
}

function ReadRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] items-baseline gap-3 px-5 py-2.5">
      <span className="text-sm text-muted">{label}</span>
      <span className={mono ? 'font-mono text-xs text-ink' : 'text-sm text-ink'}>
        {value ?? <span className="text-faint">—</span>}
      </span>
    </div>
  );
}

function SaveBar({
  dirty,
  resendable = false,
  blocked = null,
  hint = null,
  capability = 'device.write',
  pending,
  onSave,
  onReset,
}: {
  dirty: boolean;
  /**
   * There are current values to write even with no edit pending, so the button
   * stays live to re-push them. A CPE can accept a task and never run it (an ONU
   * that was offline, a connection request it ignored), and the value silently
   * never lands — resending the same thing is the fix, so the form must let you.
   */
  resendable?: boolean;
  /**
   * A sentence naming what stops the pending edit from being sent — a refusal
   * by name, shown in place of the hint with the button held. The draft stays
   * a draft (Discard offered, the panel keeps it mounted) until the operator
   * clears the block or drops the edit; a dead Apply click would have said
   * nothing. Red only while there IS an edit: on a clean form it is the
   * reason Re-apply is not offered (a PPPoE row's password, a row the CPE
   * has drifted from), said plainly — a card nobody touched is not in error.
   */
  blocked?: string | null;
  /**
   * What the button does, in place of the default "Sent to the device
   * immediately" / "Resends the current values" — for a bar whose Apply is
   * not a parameter write to the CPE (the WAN card's IPv6 bar records on the
   * VLAN row and runs in the tray; review, 2026-09-14: the default sentence
   * promised a CPE write the Huawei-chassis path never makes).
   */
  hint?: string | null;
  /** The capability the bar's writes need — the same one that makes its fields live. */
  capability?: Capability;
  pending: boolean;
  onSave: () => void;
  onReset: () => void;
}) {
  const { can } = useSession();
  // Tells the accordion panel above (if any) there is an edit in progress,
  // so the group closing this card for a sibling keeps it mounted rather
  // than losing the draft. Before the capability check: a hook, and a reader
  // has no draft to report anyway.
  useUnsavedDraft(dirty);
  if (!can(capability)) return null;

  const enabled = (dirty || resendable) && !blocked;
  return (
    <div className="flex items-center gap-2 border-t border-line px-5 py-3">
      <Button
        variant="primary"
        size="sm"
        icon={<Save className="size-3.5" />}
        disabled={!enabled}
        loading={pending}
        onClick={onSave}
      >
        {dirty ? 'Apply' : 'Re-apply'}
      </Button>
      {dirty && (
        <Button size="sm" onClick={onReset}>
          Discard
        </Button>
      )}
      {blocked ? (
        <span className={`text-xs ${dirty ? 'text-danger' : 'text-muted'}`}>{blocked}</span>
      ) : (
        enabled && (
          <span className="text-xs text-faint">
            {hint ??
              (dirty
                ? 'Sent to the device immediately'
                : 'Resends the current values — use if a write never reached the device')}
          </span>
        )
      )}
    </div>
  );
}

// --- cards -------------------------------------------------------------------

/** Local metadata: the alias and site are ours, not the CPE's. */
/**
 * A parameter tree the ACS has never actually READ.
 *
 * A freshly registered CPE — the whole fleet, after the operator's GenieACS
 * wipe — holds only what the sweep's projection asked for: names and
 * addresses. Every WLAN's Enable node exists with NO value, and the WAN
 * connection object is empty. The page then showed every possible SSID as
 * live (unknown !== false) and a blank WAN, until the nightly 1–5 am batch
 * happened to reach the device.
 */
export function deviceTreeUnread(device: DeviceDetail): boolean {
  const wifiUnread =
    device.wlans.length > 0 && device.wlans.every((w) => w.enabled === null || w.enabled === undefined);
  // A management-only ONU (its only connection is the TR-069 channel — bridged
  // subscriber, no WAN of its own) reads as mode 'unknown' with no address, but
  // it is NOT unread: the tree was read fine, there is simply no subscriber WAN.
  // Treating it as unread summoned it on every page open for a value that will
  // never arrive.
  const wanManagementOnly =
    !device.wan.externalIp &&
    device.wan.candidates.length > 0 &&
    device.wan.candidates.every((c) => c.management);
  const wanUnread =
    device.wan.mode === 'unknown' && !device.wan.externalIp && !wanManagementOnly;
  return wifiUnread || wanUnread;
}

/**
 * The header node for each detail card, kept here beside the card it titles.
 *
 * On the ONU page these cards render `embedded`, so the enclosing Collapsible is
 * the one and only header — it draws these exact nodes as its bar, which is why
 * they are exported rather than inlined: the title (and its status pill) has to
 * read the same whether the card is expanded or collapsed to a bar.
 */
export function wanCardTitle(
  device: DeviceDetail,
  /** The platform's recorded firewall (`onus.firewalled`), the pill's fallback where the CPE reports none. */
  recordedFirewall?: boolean | null,
) {
  const managementOnly =
    !device.wan.externalIp &&
    device.wan.candidates.length > 0 &&
    device.wan.candidates.every((c) => c.management);
  /*
    A BRIDGED subscriber connection — every HS hotspot unit (operator,
    2026-10-10: "why are HS onus showing wan internet instead of wan bridge").
    The bar used to say "Internet" whatever the connection was, and printed
    the DHCP leaf a bridge carries but never uses. It now reads like every
    other bridge card: Bridge, and whether it is passing traffic — no
    addressing, IPv6 or firewall, which a bridge does not have.
  */
  const primary = device.wan.candidates.find((c) => c.primary) ?? null;
  if (primary?.role === 'bridge') {
    return (
      <span className="flex items-center gap-2">
        <Globe className="size-4 text-muted" />
        WAN
        <Pill tone="info" title="Passed straight through: the router does not terminate it. A hotspot VLAN reaches its SSID this way.">
          Bridge
        </Pill>
        {/* No "passing through" pill: a bridge has no session to be up or down (operator, 2026-10-28). */}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <Globe className="size-4 text-muted" />
      WAN
      {/* What this connection is for, as every other WAN card says it (operator, 2026-10-05). */}
      {!managementOnly && (
        <Pill tone="primary" title="Routed by the router: it terminates the connection and serves its LAN.">
          Internet
        </Pill>
      )}
      <Pill tone={managementOnly ? 'neutral' : device.wan.mode === 'pppoe' ? 'primary' : 'neutral'}>
        {/* Not "management only": beside the management card it read as a second management
            connection (operator, 2026-10-14: "now we have 2 management interfaces"). This card is
            where a subscriber WAN is made when the router has none. */}
        {managementOnly ? 'no subscriber WAN' : WAN_MODE_LABELS[device.wan.mode]}
      </Pill>
      {/* A PPPoE that works with a password SimpleACS never held — set before
          the platform, or on the router's own page (operator, 2026-10-21:
          "show that there was a password applied outside its control"). A
          Resync leaves such a connection alone; this says why the account is
          not the platform's to re-apply. */}
      {!managementOnly && device.wan.mode === 'pppoe' && primary?.connected && !device.wan.pppoePasswordHeld && (
        <Pill
          state
          tone="warn"
          title="This PPPoE connection is up with a password SimpleACS does not hold — it was set before SimpleACS, or on the router's own page. Resync leaves it alone; enter the account in the WAN dialog to bring it under SimpleACS."
        >
          Password outside SimpleACS
        </Pill>
      )}
      {/* The IPv6 half as ONE pill — the ONU card's title-bar pill, shared
          (`ipv6TitlePill`; operator, 2026-09-14: "move the ipv6 pill to the
          title bar") — as the CPE REPORTS it on the scored subscriber WAN,
          never the record, because a dual-stack record can be applied and
          deliver nothing until the upstream offers RA/DHCPv6. The observed
          words are its tooltip; green only on the CPE's own Connected. */}
      {!managementOnly &&
        (() => {
          const pill = ipv6TitlePill(device.wan.ipv6);
          return pill ? (
            <Pill state tone={pill.tone} title={pill.title}>
              {pill.label}
            </Pill>
          ) : null;
        })()}
      {/* The router's firewall, as the ONU card says it (operator, 2026-10-09:
          "show firewall pill too for routed wan and mngt"). */}
      {!managementOnly &&
        (() => {
          const pill = firewallTitlePill(device.firewall, recordedFirewall);
          return pill ? <FirewallPill pill={pill} /> : null;
        })()}
    </span>
  );
}

export function lanCardTitle(device: DeviceDetail) {
  return (
    <span className="flex items-center gap-2">
      <Network className="size-4 text-muted" />
      LAN
      {device.lan.dhcpEnabled !== null && (
        <Pill state tone={device.lan.dhcpEnabled ? 'success' : 'neutral'}>
          DHCP {device.lan.dhcpEnabled ? 'on' : 'off'}
        </Pill>
      )}
    </span>
  );
}

export function wifiCardTitle(device: DeviceDetail) {
  const active = device.wlans.filter((w) => w.enabled !== false).length;
  const statesUnread = device.wlans.every((w) => w.enabled === null || w.enabled === undefined);
  return (
    <span className="flex items-center gap-2">
      <Wifi className="size-4 text-muted" />
      WiFi
      {device.wlans.length > 0 && (
        <span className="text-xs font-normal text-faint">
          {statesUnread
            ? 'states not read yet — waking the CPE'
            : `${active} of ${device.wlans.length} SSIDs live`}
        </span>
      )}
    </span>
  );
}

/**
 * Summon automatically, once per visit, when the tree was never read.
 *
 * The device answers a connection request in seconds and the page's own
 * polling picks the values up — WiFi Enable states and the WAN fill in
 * without anybody knowing a button existed. One shot per mount: a device
 * that refuses the request must not be summoned every fifteen seconds
 * forever.
 *
 * It lived in the Device panel of the Configs tab, which mounted its body
 * only when opened — so from the 2026-09-05 restructure until the panel was
 * removed (operator, 2026-09-13: "remove device") it fired only for whoever
 * expanded that panel. Now the Configs tab's container calls it, with the
 * same one-shot guard, so opening the tab on an unread CPE is what wakes it.
 * `device` is null until the query answers; the shot fires when it arrives.
 */
export function useSummonUnreadOnce(
  device: DeviceDetail | null,
  onNotice: (message: string) => void,
) {
  const queryClient = useQueryClient();
  const summoned = useRef(false);
  useEffect(() => {
    if (!device || summoned.current || !deviceTreeUnread(device)) return;
    summoned.current = true;
    api
      .post(`/devices/${encodeURIComponent(device.id)}/actions`, { action: 'summon' })
      .then(() => {
        onNotice('This CPE had never been fully read — summoned it; WiFi and WAN details fill in over the next refresh.');
        // Two delayed re-reads: the CPE typically answers within seconds.
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ['device', device.id] }), 8000);
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ['device', device.id] }), 20000);
      })
      .catch(() => {
        // A device that cannot be summoned is reported by every other surface
        // already; failing quietly here beats a toast on every page open.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?.id]);
}

/**
 * What the ACS knows about this CPE that the overview does not already show —
 * the read rows of the old Device panel (operator, 2026-09-13: "Management
 * Server (rename to ACS Details), remove device"). Serial, manufacturer, model
 * and firmware stayed on the overview; these five sit at the top of the ACS
 * details panel, compact, above the management-server controls, because they
 * are all answers to "is the ACS hearing from it, and as what".
 */
export function AcsDeviceFacts({ device }: { device: DeviceDetail }) {
  const facts: Array<{ label: string; value: string | null; mono?: boolean }> = [
    { label: 'Last inform', value: formatTimestamp(device.lastInform) },
    { label: 'Uptime', value: formatUptime(device.uptimeSeconds) },
    { label: 'Management IP', value: device.mgmtIp, mono: true },
    { label: 'Data model', value: device.dataModel.toUpperCase() },
    { label: 'GenieACS id', value: device.id, mono: true },
  ];
  return (
    <dl className="grid gap-x-6 gap-y-2.5 border-b border-line px-5 py-3.5 sm:grid-cols-2 lg:grid-cols-3">
      {facts.map((fact) => (
        <div key={fact.label} className="min-w-0">
          <dt className="text-xs text-muted">{fact.label}</dt>
          <dd
            className={fact.mono ? 'truncate font-mono text-xs text-ink' : 'text-sm text-ink'}
            title={fact.value ?? undefined}
          >
            {fact.value ?? <span className="text-faint">—</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The platform's own record of the CPE: the display name the Firmware page
 * and the Topology pickers show for it, and the zone/splitter pairing the
 * topology falls back to when the ONU row has no placement of its own. The
 * editable half of the old Device panel — kept, under the ACS details, because
 * a write that works is never taken away (the ONU's Placement card writes the
 * ONU row, not this one).
 */
export function DeviceRecordEditor({
  device,
  onNotice,
}: {
  device: DeviceDetail;
  onNotice: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  // Guarded like the other cards: the name field is safe from a plain refetch
  // (alias only changes on save) but goes through the same hook so no form on
  // this page can be clobbered mid-edit. Zone/splitter seed once and are never
  // re-synced, so they need no guard. See useSyncedDraft.
  const {
    draft: alias,
    baseline: aliasBaseline,
    edit: setAlias,
    commit: commitAlias,
  } = useSyncedDraft(device.alias ?? '', [device.alias]);
  const [zoneId, setZoneId] = useState((device as { zoneId?: string | null }).zoneId ?? '');
  const [splitterId, setSplitterId] = useState(
    (device as { splitterId?: string | null }).splitterId ?? '',
  );

  const topologyQuery = useQuery({
    queryKey: ['topology'],
    queryFn: () => api.get<Topology>('/topology'),
  });
  const zonesList = topologyQuery.data?.zones ?? [];
  // Splitters narrow to the chosen zone, since that is where the fibre runs.
  const splittersList = (topologyQuery.data?.splitters ?? []).filter(
    (s) => !zoneId || s.zoneId === zoneId,
  );

  const dirty =
    alias !== aliasBaseline ||
    zoneId !== ((device as { zoneId?: string | null }).zoneId ?? '') ||
    splitterId !== ((device as { splitterId?: string | null }).splitterId ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patch<DeviceDetail>(`/devices/${encodeURIComponent(device.id)}`, {
        alias,
        zoneId: zoneId || null,
        splitterId: splitterId || null,
      }),
    onSuccess: async () => {
      commitAlias();
      onNotice('Name saved');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['device', device.id] }),
        // The ONU page's title and the ONU list both show this name now, and
        // waiting for their next background refresh made the card and the
        // heading disagree for fifteen seconds after every save.
        queryClient.invalidateQueries({ queryKey: ['onu'] }),
        queryClient.invalidateQueries({ queryKey: ['onus'] }),
      ]);
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not save'),
  });

  return (
    <div className="border-t border-line">
      <div className="px-5 pt-3 text-xs font-medium uppercase tracking-wide text-faint">
        ACS record
      </div>
      <div className="divide-y divide-line">
        <div className="grid grid-cols-[9rem_1fr] items-center gap-3 px-5 py-2.5">
          <label className="text-sm text-muted">Name</label>
          {can('device.write') ? (
            <div>
              <Input
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder="Subscriber name or reference"
              />
              <p className="mt-1 text-xs text-faint">
                One name everywhere: for a unit on a chassis this renames it on the chassis, the ONU
                page and here.
              </p>
            </div>
          ) : (
            <span className="text-sm text-ink">{alias || '—'}</span>
          )}
        </div>
        <div className="grid grid-cols-[9rem_1fr] items-center gap-3 px-5 py-2.5">
          <label className="text-sm text-muted">Zone</label>
          {can('device.write') ? (
            <select
              className="input"
              value={zoneId}
              onChange={(e) => {
                setZoneId(e.target.value);
                setSplitterId('');
              }}
            >
              <option value="">No zone</option>
              {zonesList.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.parentId ? '— ' : ''}
                  {zone.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-ink">
              {zonesList.find((z) => z.id === zoneId)?.name ?? '—'}
            </span>
          )}
        </div>
        <div className="grid grid-cols-[9rem_1fr] items-center gap-3 px-5 py-2.5">
          <label className="text-sm text-muted">Splitter</label>
          {can('device.write') ? (
            <select
              className="input"
              value={splitterId}
              onChange={(e) => setSplitterId(e.target.value)}
            >
              <option value="">No splitter</option>
              {splittersList.map((splitter) => (
                <option key={splitter.id} value={splitter.id}>
                  {splitter.parentId ? '— ' : ''}
                  {splitter.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-ink">
              {splittersList.find((s) => s.id === splitterId)?.name ?? '—'}
            </span>
          )}
        </div>
      </div>
      <SaveBar
        dirty={dirty}
        pending={save.isPending}
        onSave={() => save.mutate()}
        onReset={() => setAlias(device.alias ?? '')}
      />
    </div>
  );
}

const WAN_MODE_LABELS: Record<WanMode, string> = {
  dhcp: 'DHCP',
  static: 'Static',
  pppoe: 'PPPoE',
  unknown: 'Unknown',
};

/**
 * What the WAN card's IPv6 rows need to SAVE — the ONU the VLAN row belongs
 * to and its chassis, exactly what the WAN mode wizard is given, because the
 * card's Save runs the wizard's own sequence (`useWanApply`). Absent, the
 * rows are read-only: the page has no ONU context to write through.
 */
export interface WanCardIpv6Apply {
  oltId: string;
  onuId: string;
  vendor: string | null;
  serial: string;
  /** Where the background toast/tray row links — the ONU's own page. */
  href?: string;
  /** Extra query keys to refresh once the write lands (the page's own). */
  invalidate?: QueryKey[];
  /**
   * The platform's own firewall column (`onus.firewalled`, off the OMCI-config
   * read) — where the card's Firewall row opens when the CPE reports no
   * firewall of its own (`defaultFirewall`: the CPE's reading, else this,
   * else assumed from the mode). Null = never recorded.
   */
  firewalled?: boolean | null;
}

export function WanCard({
  device,
  onNotice,
  embedded,
  onEditIpv6,
  ipv6Apply,
}: {
  device: DeviceDetail;
  onNotice: (message: string) => void;
  embedded?: boolean;
  /** Opens the WAN mode wizard — the bridged/routed question and a VLAN not attached yet live there. */
  onEditIpv6?: () => void;
  /**
   * The ONU context the card writes through — the ONU, its chassis, where the
   * tray row links, what to refresh, and the recorded firewall. Absent (a
   * router with no ONU), the card rebuilds the router's own WAN over TR-069.
   */
  ipv6Apply?: WanCardIpv6Apply;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();

  const attachedQuery = useQuery({
    queryKey: ['device', device.id, 'vlans'],
    queryFn: () => api.get<Page<OnuVlan>>(`/devices/${encodeURIComponent(device.id)}/vlans`),
  });
  const attachedVlans = attachedQuery.data?.items ?? [];

  /**
   * The IPv6 half (operator, 2026-09-13/14): what the CPE READS, and where an
   * intent would land, from the `wan-ipv6` read the WAN mode dialog shares
   * (its query key), so the card and the dialog can never disagree. Until the
   * read answers, the device document's own reading of the same connection.
   */
  const ipv6Base = `/devices/${encodeURIComponent(device.id)}`;
  const ipv6Row = subscriberWanRow(attachedVlans, device.wan.vlanId);
  const ipv6Mode = ipv6Row?.mode ?? 'route';
  const ipv6Query = useQuery({
    queryKey: wanIpv6QueryKey(ipv6Base, ipv6Mode),
    queryFn: () => api.get<Ipv6TargetRead>(`${ipv6Base}/wan-ipv6?mode=${encodeURIComponent(ipv6Mode)}`),
  });
  const ipv6Observed = ipv6Query.data ? ipv6Query.data.observed : device.wan.ipv6;
  const ipv6 = observedIpv6Rows(ipv6Observed);
  /** The IPv6 DATA the CPE holds — read-only, under the IPv4 data (operator, 2026-09-14). */
  const ipv6Data = observedIpv6Data(ipv6Observed);

  /**
   * What can be chosen: the VLANs attached here, then any the ONU is already
   * carrying that this platform has no record of. Management (and VoIP) never
   * — pointing the subscriber's WAN at it is how an ONU stops answering.
   */
  const vlanOptions = useMemo(() => {
    const rows = attachedVlans
      .filter((vlan) => vlan.purpose !== 'management' && vlan.purpose !== 'voip')
      .map((vlan) => ({
        value: String(vlan.vlanId),
        label: String(vlan.vlanId),
        hint: [vlan.label, vlan.role === 'primary' ? 'primary' : null].filter(Boolean).join(' · ') || null,
      }));
    const known = new Set(rows.map((r) => r.value));
    for (const candidate of device.wan.candidates) {
      if (!candidate.vlanId || candidate.management || known.has(candidate.vlanId)) continue;
      known.add(candidate.vlanId);
      rows.push({ value: candidate.vlanId, label: candidate.vlanId, hint: 'on the ONU' });
    }
    return rows.sort((a, b) => Number(a.value) - Number(b.value));
  }, [attachedVlans, device.wan.candidates]);

  const initial = {
    mode: device.wan.mode,
    pppoeUsername: device.wan.pppoeUsername ?? '',
    pppoePassword: device.wan.pppoePassword ?? '',
    vlanId: device.wan.vlanId ?? '',
    externalIp: device.wan.externalIp ?? '',
    subnetMask: device.wan.subnetMask ?? '',
    gateway: device.wan.gateway ?? '',
    dns: device.wan.dns ?? '',
  };
  // A background refetch must not wipe an in-progress edit — see useSyncedDraft.
  const { draft: form, baseline, edit: setForm, commit, reset } = useSyncedDraft(initial, [
    device.wan.mode,
    device.wan.pppoeUsername,
    device.wan.pppoePassword,
    device.wan.vlanId,
    device.wan.externalIp,
    device.wan.subnetMask,
    device.wan.gateway,
    device.wan.dns,
  ]);
  const modeChanged = form.mode !== baseline.mode;

  /** Whose capability the card writes under: the ONU's on its page, the router's on its own. */
  const writeCap: Capability = ipv6Apply ? 'onu.provision' : 'device.write';
  const editable = can(writeCap);

  /** The VLAN row the WAN rides — the one for the VLAN the card names, or none yet (Apply attaches it). */
  const vlanNumber = Number(form.vlanId);
  const wanRow: OnuVlan | null =
    attachedVlans.find((v) => v.vlanId === vlanNumber && v.purpose !== 'management' && v.purpose !== 'voip') ?? null;

  /**
   * WHO manages the WAN (operator, 2026-10-07: "set the wan with the option
   * of omci (olt only) or tr069, but the other onu mngt like lan, wifi etc can
   * remain tr069"). Opens on the record; with none, on what the server's dry
   * run says writes this unit's WAN today (an OLT-made `omci_*` → the OLT);
   * with neither, the router — the platform's default. Said beside the radios
   * so a pre-selection is never mistaken for a choice somebody made.
   */
  const zte = ipv6Apply?.vendor === 'zte';
  const huaweiOnt = /^hwtc/i.test(device.serial ?? '') || /huawei/i.test(device.manufacturer ?? '');
  const writerQuery = useQuery({
    queryKey: ['onu', ipv6Apply?.onuId ?? '', 'omci-wan-writer', vlanNumber || 0, 'set'],
    queryFn: () =>
      api.post<{ writer?: SubscriberWanWriter }>(
        `/olts/${encodeURIComponent(ipv6Apply!.oltId)}/onus/${encodeURIComponent(ipv6Apply!.onuId)}/omci-wan`,
        wanWriterProbeBody({ vlanId: vlanNumber }),
      ),
    enabled: Boolean(ipv6Apply) && zte && vlanNumber > 0 && can('onu.provision'),
    staleTime: 60_000,
  });
  const probed = writerQuery.data?.writer ?? null;
  const ownerFromWriter: WanOwner | null =
    probed === 'zte_chassis' || probed === 'huawei_chassis' ? 'omci' : probed ? 'tr069' : null;
  const recordedOwner: WanOwner | null = wanRow?.wanOwner ?? null;
  const defaultOwner: WanOwner = recordedOwner ?? ownerFromWriter ?? 'tr069';
  const [ownerPick, setOwnerPick] = useState<WanOwner | null>(null);
  const owner: WanOwner = ipv6Apply ? (ownerPick ?? defaultOwner) : 'tr069';
  const ownerDirty = ownerPick !== null && ownerPick !== defaultOwner;
  const ownerSource = recordedOwner
    ? 'recorded'
    : ownerFromWriter === 'omci'
      ? "today the router's WAN is the OLT-made one"
      : ownerFromWriter === 'tr069'
        ? 'today the router terminates its own WAN'
        : 'not chosen yet — the router is the default';
  const oltRefusal =
    zte && huaweiOnt
      ? `${device.serial} is a Huawei ONT on a ZTE OLT — the ZTE wan-ip line does nothing on one, so its WAN can only be the router's.`
      : null;

  /**
   * The IPv6 rows (operator, 2026-09-14), judged by the shared gate exactly as
   * the wizard judges them. They are the ROUTER's leaves: with the OLT
   * managing the WAN they are greyed with that sentence, and nothing of IPv6
   * is sent.
   */
  const ipv6Target = ipv6Query.data ?? null;
  const ipv6Judge = ipv6Target ? (intent: WanIpv6) => ipv6Refusals(intent, ipv6Target.target) : null;
  const ipv6Matrix = ipv6Judge ? ipv6ChoiceMatrix(ipv6Judge) : null;
  const ipv6Default =
    ipv6Judge && ipv6Matrix
      ? defaultIpv6Rows({ record: ipv6Row?.ipv6, observed: ipv6Target?.observed, refusals: ipv6Judge, matrix: ipv6Matrix })
      : null;
  const [ipv6Pick, setIpv6Pick] = useState<{ address: Ipv6RowChoice | null; prefix: Ipv6PrefixMode | null }>({
    address: null,
    prefix: null,
  });
  const ipv6Rows = ipv6Default
    ? { address: ipv6Pick.address ?? ipv6Default.address, prefix: ipv6Pick.prefix ?? ipv6Default.prefix }
    : null;
  const ipv6Intent: WanIpv6 | null = ipv6Rows ? ipv6IntentFromRows(ipv6Rows) : null;
  const ipv6Editable = Boolean(ipv6Apply) && can('onu.provision') && ipv6Rows !== null && owner === 'tr069';
  const ipv6ReadFailed = ipv6Query.isError && ipv6Target === null;
  const ipv6Dirty =
    ipv6Editable &&
    ipv6Rows !== null &&
    ipv6Default !== null &&
    (ipv6Rows.address !== ipv6Default.address || ipv6Rows.prefix !== ipv6Default.prefix);
  /**
   * What IPv6 rides the rebuild: the rows when the operator moved one, else
   * the RECORD re-applied (a rebuilt connection starts on the firmware's
   * default) — never a default nobody chose: null means not managed, and ~130
   * live WANs are dual-stack by firmware default beside a plain record.
   */
  const ipv6ToSend: WanIpv6 | null = owner !== 'tr069' ? null : ipv6Dirty ? ipv6Intent : (ipv6Row?.ipv6 ?? null);
  const ipv6RefusalsNow: string[] = ipv6Dirty && ipv6Intent && ipv6Judge ? ipv6Judge(ipv6Intent) : [];
  /**
   * The IPv6 SWITCH and the radios it shows (operator, 2026-10-21: "hide
   * options not supported … but have a switch to enable/disable IPv6"): off
   * is the pair (None, None) — IPv4 only; on is the first pair this unit can
   * take (`ipv6SwitchOnPair`); a mode refused with every pairing is hidden
   * until the platform learns its word (`visibleIpv6Choices`); a unit with no
   * writable dual-stack pair cannot be switched on and the switch says why.
   */
  const ipv6On = ipv6IsOn(ipv6Rows);
  const ipv6OnPair =
    ipv6Judge && ipv6Matrix
      ? ipv6SwitchOnPair({ record: ipv6Row?.ipv6, observed: ipv6Target?.observed, refusals: ipv6Judge, matrix: ipv6Matrix })
      : null;
  const ipv6SwitchBlocked = !ipv6On && ipv6OnPair === null;
  const ipv6SwitchReason = ipv6SwitchBlocked && ipv6Matrix ? ipv6SwitchOffReason(ipv6Matrix) : null;
  const ipv6Visible = ipv6Matrix ? visibleIpv6Choices(ipv6Matrix, ipv6Rows, ipv6Row?.ipv6) : { address: [], prefix: [] };

  /**
   * The Firewall row (operator, 2026-09-14), opening where the wizard's
   * Firewall step opens (`defaultFirewall`), the source said beside it. It
   * rides the Apply as the standalone verb, LAST, and only when it moved.
   */
  const firewallDefault = defaultFirewall({
    cpe: device.firewall,
    recorded: ipv6Apply?.firewalled ?? null,
    mode: ipv6Row?.mode ?? null,
  });
  const [firewallPick, setFirewallPick] = useState<boolean | null>(null);
  const firewallValue = firewallPick ?? firewallDefault.value;
  const firewallEditable = Boolean(ipv6Apply) && can('onu.provision');
  const firewallDirty = firewallEditable && firewallPick !== null && firewallPick !== firewallDefault.value;

  const wanApply = useWanApply({
    base: ipv6Base,
    oltId: ipv6Apply?.oltId ?? '',
    onuId: ipv6Apply?.onuId ?? '',
    serial: ipv6Apply?.serial ?? device.serial,
    ...(ipv6Apply?.href ? { href: ipv6Apply.href } : {}),
    invalidate: [['device', device.id], ...(ipv6Apply?.invalidate ?? [])],
  });

  /**
   * The only connection the CPE reports over TR-069 is its management one —
   * no subscriber WAN to show HERE (a routed ONU may keep its WAN on the
   * chassis). Not the same as bridged, and never the management address as
   * the WAN.
   */
  const managementOnly =
    !device.wan.externalIp &&
    device.wan.candidates.length > 0 &&
    device.wan.candidates.every((c) => c.management);
  const primary = device.wan.candidates.find((c) => c.primary) ?? null;

  /**
   * ONE Apply for the whole card (operator, 2026-10-07: "when you apply
   * settings even if they are identical with existing, delete and reapply").
   * The WAN is deleted and built again from what the card says, through the
   * one writer the owner names — a DHCP → PPPoE change REPLACES the
   * connection, in this card, instead of standing a second one up beside it.
   * Always live: a clean card re-applies, which is the point.
   */
  const addressing: VlanAddressing | null = form.mode === 'unknown' ? null : form.mode;
  const passwordStored = Boolean(wanRow?.hasPppoePassword) || Boolean(device.wan.pppoePassword);
  const blocked: string | null = !form.vlanId
    ? 'Choose the VLAN the WAN rides.'
    : !addressing
      ? 'Choose DHCP, Static or PPPoE.'
      : addressing === 'pppoe' && !form.pppoeUsername.trim()
        ? 'PPPoE needs a username.'
        : addressing === 'pppoe' && !form.pppoePassword && !passwordStored
          ? 'Type the PPPoE password — none is stored for this account.'
          : addressing === 'static' && (!form.externalIp || !form.subnetMask || !form.gateway)
            ? 'A static WAN needs its address, mask and gateway.'
            : owner === 'omci' && oltRefusal
              ? oltRefusal
              : (ipv6RefusalsNow[0] ?? null);
  const particularsTyped =
    modeChanged ||
    form.pppoeUsername !== baseline.pppoeUsername ||
    form.pppoePassword !== baseline.pppoePassword ||
    form.externalIp !== baseline.externalIp ||
    form.subnetMask !== baseline.subnetMask ||
    form.gateway !== baseline.gateway;
  const dirty =
    modeChanged ||
    particularsTyped ||
    form.vlanId !== baseline.vlanId ||
    form.dns !== baseline.dns ||
    ownerDirty ||
    ipv6Dirty ||
    firewallDirty;

  const rebuildDevice = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ message: string }>(`/devices/${encodeURIComponent(device.id)}/wan`, body),
    onSuccess: async (result) => {
      onNotice(result.message);
      commit();
      await queryClient.invalidateQueries({ queryKey: ['device', device.id] });
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'The WAN was not rebuilt'),
  });

  const applyWan = () => {
    if (blocked || !addressing) return;
    const who = owner === 'omci' ? 'the OLT' : 'the router';
    if (
      !confirm(
        `Rebuild this WAN as ${WAN_MODE_LABELS[addressing]} on VLAN ${form.vlanId}, managed by ${who}?\n\nIt is deleted and built again — the subscriber drops for a few seconds.`,
      )
    ) {
      return;
    }
    if (!ipv6Apply) {
      // A router with no ONU: its own WAN, over TR-069.
      rebuildDevice.mutate({
        mode: addressing,
        vlanId: form.vlanId,
        connectionType: 'routed',
        rebuild: true,
        ...(addressing === 'pppoe'
          ? { username: form.pppoeUsername.trim(), ...(form.pppoePassword ? { password: form.pppoePassword } : {}) }
          : {}),
        ...(addressing === 'static'
          ? { staticIp: form.externalIp, subnetMask: form.subnetMask, gateway: form.gateway, ...(form.dns ? { dns: form.dns } : {}) }
          : {}),
      });
      return;
    }
    const choice: WanApplyChoice = {
      ask: 'card',
      zte,
      vlanId: vlanNumber,
      row: wanRow,
      mode: 'route',
      addressing,
      particularsTyped,
      pppoeUsername: form.pppoeUsername,
      // Typed, or nothing: the field opens on the kept one, which can be stale.
      pppoePassword: cardPasswordToSend({
        shown: form.pppoePassword,
        opened: baseline.pppoePassword,
        recorded: Boolean(wanRow?.hasPppoePassword),
      }),
      staticIp: form.externalIp,
      staticMask: form.subnetMask,
      staticGateway: form.gateway,
      staticDns: form.dns,
      ipv6: ipv6ToSend,
      firewalled: firewallDirty ? firewallValue : undefined,
      owner,
    };
    wanApply.start(choice, {
      onSettled: (ok) => {
        if (!ok) return;
        commit();
        setOwnerPick(null);
        setIpv6Pick({ address: null, prefix: null });
        setFirewallPick(null);
      },
    });
  };
  const resetWan = () => {
    reset();
    setOwnerPick(null);
    setIpv6Pick({ address: null, prefix: null });
    setFirewallPick(null);
  };

  /**
   * Delete, like a WiFi network (operator, 2026-10-07: "make wan interface
   * deletable like wifi interfaces") — `WanDeleteButton`, the same one every
   * other WAN card carries. Never the TR-069 connection; a bridge too since
   * 2026-10-14, on the bridge read-out below as well as the editor.
   */
  const deletable = Boolean(primary) && wanDeletable(primary!) && can(writeCap);
  const deleteEndpoint = ipv6Apply
    ? `/olts/${encodeURIComponent(ipv6Apply.oltId)}/onus/${encodeURIComponent(ipv6Apply.onuId)}/wan`
    : `/devices/${encodeURIComponent(device.id)}/wan`;
  const removedRow = wanRow?.wanRemovedAt ? wanRow : (ipv6Row?.wanRemovedAt ? ipv6Row : null);

  const pending = wanApply.running || rebuildDevice.isPending;

  /*
    The subscriber connection is a BRIDGE (a bridged ONU — the operator's
    `HS ` units among them — or any subscriber whose own router does the
    routing). The editor below is a ROUTED rebuild: its Apply — and the
    always-live Re-apply on a clean card — deletes the connection and builds a
    routed DHCP/static/PPPoE WAN in its place, re-roling a bridged subscriber
    by accident. So a bridge gets what it is: a read-out, the WAN mode dialog
    — where routed-or-bridged is a deliberate question — for changing it, and
    its Delete (2026-10-14, operator: "1_INTERNET_B_VID_ has no delete button"
    — this read-out returned before the Delete was drawn).
  */
  if (primary?.role === 'bridge') {
    return (
      <Card embedded={embedded} title={wanCardTitle(device)}>
        <div className="divide-y divide-line">
          <WanConnectionLine connection={primary} />
          <p className="px-5 py-3 text-sm text-muted">
            A bridge: the router passes this connection straight through, so it has no addressing,
            IPv6 or firewall of its own — whatever is behind it does that.
            {onEditIpv6 && can('onu.provision') ? ' To make it routed, or move it, use the WAN mode dialog.' : ''}
          </p>
          <ReadRow label="VLAN" value={primary.vlanId} />
          <ReadRow label="Connection type" value={primary.connectionType} />
          <ReadRow
            label="WAN MAC"
            value={primary.macAddress ? <CopyableValue value={primary.macAddress} /> : <span className="text-faint">not reported</span>}
          />
          {primary.connected && primary.uptimeSeconds !== null && primary.uptimeSeconds > 0 && (
            <ReadRow label="Uptime" value={formatUptime(primary.uptimeSeconds)} />
          )}
          {wanPortTarget(primary) ? (
            <WanPorts
              connection={primary}
              base={`/devices/${encodeURIComponent(device.id)}`}
              serial={ipv6Apply?.serial ?? device.serial}
              editable={can(writeCap)}
              href={ipv6Apply?.href}
            />
          ) : (
            <ReadRow
              label="Serves"
              value={primary.serves.length > 0 ? primary.serves.join(', ') : <span className="text-faint">no socket or SSID bound</span>}
            />
          )}
        </div>
        {onEditIpv6 && can('onu.provision') && (
          <div className="flex items-center gap-2 border-t border-line px-5 py-3">
            <Button size="sm" onClick={onEditIpv6}>
              WAN mode…
            </Button>
          </div>
        )}
        {deletable && (
          <div className="border-t border-line">
            <WanDeleteButton
              connection={primary}
              deviceId={device.id}
              endpoint={deleteEndpoint}
              serial={ipv6Apply?.serial ?? device.serial}
              href={ipv6Apply?.href}
              invalidate={[['device', device.id], ...(ipv6Apply?.invalidate ?? [])]}
            />
          </div>
        )}
      </Card>
    );
  }

  /*
    No subscriber WAN: an empty card that says so, not the editor with every
    field unselected (operator, 2026-10-14: "if theres no wan, just show an
    empty card saying no wan"). WAN mode makes one; ports attach to it there.
  */
  if (!primary) {
    const recordedBridge = wanRow?.mode === 'bridge' ? wanRow.vlanId : null;
    return (
      <Card embedded={embedded} title={wanCardTitle(device)}>
        <div className="divide-y divide-line">
          {removedRow && (
            <div className="px-5 py-3 text-sm text-muted">
              <span className="font-medium text-ink">The WAN on VLAN {removedRow.vlanId} was deleted</span>{' '}
              {formatTimestamp(removedRow.wanRemovedAt!)}. Nothing builds it back on its own — Apply makes it again.
            </div>
          )}
          <p className="px-5 py-3 text-sm text-muted">
            <span className="font-medium text-ink">No WAN.</span>{' '}
            {device.wan.candidates.length === 0
              ? 'The router has not reported its WAN connections yet.'
              : `The router reports only its management connection${device.management.ip ? ` (${device.management.ip})` : ''}.`}{' '}
            {recordedBridge !== null
              ? `The record says VLAN ${recordedBridge} bridged: Resync builds that bridge on the router, or apply it from WAN mode — its ports then attach to it.`
              : 'Make one from WAN mode; ports attach to it there and on the Ports tab.'}
          </p>
        </div>
        {onEditIpv6 && can('onu.provision') && (
          <div className="flex items-center gap-2 border-t border-line px-5 py-3">
            <Button size="sm" onClick={onEditIpv6}>
              WAN mode…
            </Button>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card embedded={embedded} title={wanCardTitle(device)}>
      <div className="divide-y divide-line">
        {removedRow && (
          <div className="px-5 py-3 text-sm text-muted">
            <span className="font-medium text-ink">The WAN on VLAN {removedRow.vlanId} was deleted</span>{' '}
            {formatTimestamp(removedRow.wanRemovedAt!)}. Nothing builds it back on its own — Apply makes it again.
          </div>
        )}
        {/* Which connection the fields below edit — every other one has its own card. */}
        {primary ? <WanConnectionLine connection={primary} /> : null}
        {ipv6Apply && (
          <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-3">
            <label className="pt-0.5 text-sm text-muted">Managed by</label>
            <div>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {(['omci', 'tr069'] as const).map((o) => {
                  const refused = o === 'omci' ? oltRefusal : null;
                  const disabled = !editable || Boolean(refused);
                  return (
                    <label
                      key={o}
                      title={refused ?? undefined}
                      className={`flex items-center gap-2 text-sm ${disabled ? 'cursor-not-allowed text-faint' : 'cursor-pointer text-ink'}`}
                    >
                      <input
                        type="radio"
                        name="wan-owner"
                        checked={owner === o}
                        disabled={disabled}
                        onChange={() => setOwnerPick(o)}
                      />
                      {WAN_OWNER_LABELS[o]}
                    </label>
                  );
                })}
              </div>
              <p className="mt-1.5 text-xs text-faint">{ownerSource}</p>
              {ownerDirty && (
                <p className="mt-1.5 text-xs text-warn">
                  {owner === 'omci'
                    ? `Apply takes the router's own WAN off over TR-069, then the OLT builds this one — on a MAC the ONU chooses, not the router's${device.wan.macAddress ? ` (${device.wan.macAddress})` : ''}: a DHCP server that authenticates by MAC (RADIUS) must be told the new one. LAN, WiFi and the rest stay on the router.`
                    : "Apply takes the OLT's WAN off the chassis, then the router builds its own on the OLT WAN's MAC, so a MAC-authenticated DHCP keeps working."}
                </p>
              )}
              {owner === 'omci' && !zte && (
                <p className="mt-1.5 text-xs text-muted">
                  A Huawei OLT's own WAN form is sent marked unverified until one has run it.
                </p>
              )}
            </div>
          </div>
        )}
        {/* Firewall sits right under the connection it guards (operator, 2026-10-05). */}
        <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-3">
          <label className="pt-0.5 text-sm text-muted">Firewall</label>
          <div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {([true, false] as const).map((on) => (
                <label
                  key={on ? 'on' : 'off'}
                  className={`flex items-center gap-2 text-sm ${firewallEditable ? 'cursor-pointer text-ink' : 'cursor-not-allowed text-faint'}`}
                >
                  <input
                    type="radio"
                    name="wan-firewall"
                    checked={firewallValue === on}
                    disabled={!firewallEditable}
                    onChange={() => setFirewallPick(on)}
                  />
                  {on ? 'On' : 'Off'}
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-faint">{firewallSourceLabel(firewallDefault, ipv6Row?.mode ?? null)}</p>
            {firewallDirty && <p className="mt-1.5 text-xs text-muted">{WAN_FIREWALL_DISCLOSURE}</p>}
          </div>
        </div>
        <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-3">
          <label className="pt-0.5 text-sm text-muted">Addressing</label>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {(['dhcp', 'static', 'pppoe'] as WanMode[]).map((mode) => (
              <label
                key={mode}
                className={`flex items-center gap-2 text-sm ${editable ? 'cursor-pointer text-ink' : 'text-faint'}`}
              >
                <input
                  type="radio"
                  name="wan-mode"
                  checked={form.mode === mode}
                  disabled={!editable}
                  onChange={() => setForm({ ...form, mode })}
                />
                {WAN_MODE_LABELS[mode]}
              </label>
            ))}
          </div>
        </div>

        {/* MAC authentication needs this and it is not visible once installed. */}
        <ReadRow
          label="WAN MAC"
          value={
            device.wan.macAddress ? (
              <CopyableValue value={device.wan.macAddress} />
            ) : (
              <span className="text-faint">not reported</span>
            )
          }
        />
        <ReadRow label="Connection type" value={device.wan.connectionType} />
        <ReadRow label="Uptime" value={formatUptime(device.wan.uptimeSeconds)} />

        <div>
          <div className="px-5 pt-3 text-xs font-medium uppercase tracking-wide text-faint">IPv4</div>
          <div className="divide-y divide-line">
            {form.mode === 'static' ? (
              <>
                <EditRow
                  label="IP address"
                  path={null}
                  forceEditable={editable}
                  value={form.externalIp}
                  onChange={(externalIp) => setForm({ ...form, externalIp })}
                />
                <MaskRow
                  label="Subnet mask"
                  path={null}
                  forceEditable={editable}
                  value={form.subnetMask}
                  onChange={(subnetMask) => setForm({ ...form, subnetMask })}
                />
                <EditRow
                  label="Gateway"
                  path={null}
                  forceEditable={editable}
                  value={form.gateway}
                  onChange={(gateway) => setForm({ ...form, gateway })}
                />
                <EditRow
                  label="DNS"
                  path={null}
                  forceEditable={editable}
                  value={form.dns}
                  onChange={(dns) => setForm({ ...form, dns })}
                  hint="8.8.8.8,1.1.1.1"
                />
              </>
            ) : (
              <>
                <ReadRow label="External IP" value={device.wan.externalIp} mono />
                <ReadRow label="Subnet mask" value={subnetLabel(device.wan.subnetMask)} mono />
                <ReadRow label="Gateway" value={device.wan.gateway} mono />
                <ReadRow label="DNS" value={device.wan.dns} mono />
              </>
            )}
            {form.mode === 'pppoe' && (
              <>
                <EditRow
                  label="PPPoE user"
                  path={null}
                  forceEditable={editable}
                  value={form.pppoeUsername}
                  onChange={(pppoeUsername) => setForm({ ...form, pppoeUsername })}
                />
                <EditRow
                  label="PPPoE password"
                  path={null}
                  forceEditable={editable}
                  value={form.pppoePassword}
                  onChange={(pppoePassword) => setForm({ ...form, pppoePassword })}
                  type="password"
                  hint={passwordStored && !form.pppoePassword ? 'stored — leave empty to keep it' : undefined}
                />
              </>
            )}
          </div>
        </div>

        {/* The WAN VLAN comes from what is attached to this ONU, and from what the
            ONU already carries — a typed-in VLAN outside that chain passes nothing. */}
        <div className="grid grid-cols-[9rem_1fr] items-center gap-3 px-5 py-2.5">
          <label className="text-sm text-muted">VLAN</label>
          {editable ? (
            vlanOptions.length > 0 ? (
              <SearchSelect
                value={form.vlanId}
                placeholder="Select a VLAN"
                options={vlanOptions}
                onChange={(vlanId) => setForm({ ...form, vlanId })}
              />
            ) : (
              <span className="text-sm text-faint">
                {form.vlanId || '—'}
                <span className="ml-2 text-xs">(attach VLANs on the VLANs tab to change this)</span>
              </span>
            )
          ) : (
            <span className="text-sm text-ink">{device.wan.vlanId ?? '—'}</span>
          )}
        </div>
      </div>
      {/* The IPv6 DATA, labelled, under the IPv4 data (operator, 2026-09-14). */}
      <div className="border-t border-line">
        <div className="px-5 pt-3 text-xs font-medium uppercase tracking-wide text-faint">IPv6</div>
        <div className="divide-y divide-line">
          <ReadRow label="Address" value={ipv6Data.address} mono />
          <ReadRow label="Delegated prefix" value={ipv6Data.prefix} mono />
          <ReadRow label="Gateway" value={ipv6Data.gateway} mono />
          <ReadRow label="DNS" value={ipv6Data.dns} mono />
          <ReadRow label="Link-local" value={ipv6Data.linkLocal} mono />
          <ReadRow label="Status" value={ipv6Data.connStatus} />
        </div>
      </div>
      <div className="divide-y divide-line border-t border-line">
        <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-3">
          <label className="pt-0.5 text-sm text-muted">IPv6</label>
          <div className="flex items-start justify-between gap-3">
            <div>
              {/* The switch (operator, 2026-10-21). Off = IPv4 only on the CPE; on = the
                  first pair this unit can take. Blocked, with the gate's sentence, where
                  this unit has no writable dual-stack pair yet. */}
              <label
                className={`flex items-center gap-2 text-sm ${ipv6Editable && !ipv6SwitchBlocked ? 'cursor-pointer text-ink' : 'cursor-not-allowed text-faint'}`}
                title={ipv6SwitchReason ?? undefined}
              >
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="IPv6"
                  checked={ipv6On}
                  disabled={!ipv6Editable || ipv6SwitchBlocked}
                  onChange={(e) =>
                    setIpv6Pick(
                      e.target.checked && ipv6OnPair
                        ? { address: ipv6OnPair.address, prefix: ipv6OnPair.prefix }
                        : { address: 'none', prefix: 'none' },
                    )
                  }
                />
                {ipv6On ? 'IPv6 on — dual stack' : 'IPv6 off — IPv4 only'}
              </label>
              {ipv6SwitchBlocked && ipv6SwitchReason && !ipv6Editable === false && (
                <p className="mt-1.5 text-xs text-muted">IPv6 cannot be switched on here yet: {ipv6SwitchReason}</p>
              )}
              <p className="mt-1.5 flex flex-wrap items-baseline gap-x-2 text-xs text-faint">
                <span>
                  CPE reads: {ipv6.protocol ?? '—'}
                  {ipv6.addressMode ? ` · ${ipv6.addressMode}` : ''}
                  {ipv6.prefixMode ? ` · prefix ${ipv6.prefixMode}` : ''}
                </span>
                {ipv6.linkLocal && <span className="font-mono text-muted">{ipv6.linkLocal}</span>}
                {ipv6.connStatus && <span>IPv6 {ipv6.connStatus}</span>}
                <span>recorded: {recordedIpv6Label(ipv6Row?.ipv6)}</span>
              </p>
              {owner === 'omci' && ipv6Apply ? (
                <p className="mt-1.5 text-xs text-muted">
                  IPv6 is set on the router's own connection — choose Router (TR-069) above to set it. No OLT has an
                  IPv6 WAN form this platform has verified.
                </p>
              ) : ipv6Query.isLoading && ipv6Target === null ? (
                <p className="mt-1.5 text-xs text-muted">Reading where IPv6 would land…</p>
              ) : ipv6ReadFailed ? (
                <div className="mt-1.5 flex items-center justify-between gap-3 rounded-md border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
                  <span>
                    Could not read where IPv6 would land (
                    {ipv6Query.error instanceof Error ? ipv6Query.error.message : 'the API did not answer'}) — no IPv6
                    choice can be judged until the read succeeds.
                  </span>
                  <Button size="sm" onClick={() => void ipv6Query.refetch()}>
                    Retry
                  </Button>
                </div>
              ) : ipv6Matrix && ipv6Matrix.wholesale.length > 0 ? (
                <p className="mt-1.5 text-xs text-muted">{ipv6Matrix.wholesale[0]}</p>
              ) : null}
              {ipv6Matrix && ipv6Matrix.wholesale.length === 0 && owner === 'tr069' && (
                <div className="mt-1.5">
                  <Ipv6RefusalFold matrix={ipv6Matrix} />
                </div>
              )}
            </div>
            {onEditIpv6 && can('onu.provision') && (
              <Button size="sm" variant="ghost" onClick={onEditIpv6}>
                Edit
              </Button>
            )}
          </div>
        </div>
        {ipv6On && (
        <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-3">
          <label className="pt-0.5 text-sm text-muted">IPv6 address</label>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {ipv6Visible.address.map((choice) => {
                  const refused = ipv6Judge && ipv6Rows ? ipv6RowRefusals(ipv6Judge, ipv6Rows, { address: choice }) : [];
                  const disabled = !ipv6Editable || refused.length > 0;
                  const record = ipv6Row?.ipv6;
                  const recorded =
                    record?.protocol === 'ipv4'
                      ? choice === 'none'
                      : record?.protocol === 'dual_stack' && record.addressMode === choice;
                  return (
                    <label
                      key={choice}
                      title={refused[0]}
                      className={`flex items-center gap-2 text-sm ${disabled ? 'cursor-not-allowed text-faint' : 'cursor-pointer text-ink'}`}
                    >
                      <input
                        type="radio"
                        name="wan-ipv6"
                        checked={ipv6Rows?.address === choice}
                        disabled={disabled}
                        onChange={() => setIpv6Pick((p) => ({ ...p, address: choice }))}
                      />
                      {IPV6_ROW_LABELS[choice]}
                      {recorded && <span className="text-xs text-muted">recorded</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        )}
        {ipv6On && (
        <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-3">
          <label className="pt-0.5 text-sm text-muted">IPv6 PD</label>
          <div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {ipv6Visible.prefix.map((mode) => {
                const refused = ipv6Judge && ipv6Rows ? ipv6RowRefusals(ipv6Judge, ipv6Rows, { prefix: mode }) : [];
                const disabled = !ipv6Editable || refused.length > 0;
                const record = ipv6Row?.ipv6;
                const recorded =
                  record?.protocol === 'ipv4'
                    ? mode === 'none'
                    : record?.protocol === 'dual_stack' && record.prefixMode === mode;
                return (
                  <label
                    key={mode}
                    title={refused[0]}
                    className={`flex items-center gap-2 text-sm ${disabled ? 'cursor-not-allowed text-faint' : 'cursor-pointer text-ink'}`}
                  >
                    <input
                      type="radio"
                      name="wan-ipv6-pd"
                      checked={ipv6Rows?.prefix === mode}
                      disabled={disabled}
                      onChange={() => setIpv6Pick((p) => ({ ...p, prefix: mode }))}
                    />
                    {IPV6_PREFIX_MODE_LABELS[mode]}
                    {recorded && <span className="text-xs text-muted">recorded</span>}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        )}
      </div>
      {addressing && form.vlanId && !blocked && editable && (
        <p className="border-t border-line px-5 py-2.5 text-xs text-warn">
          {cardApplyDisclosure({ addressing, vlanId: vlanNumber || Number(form.vlanId), owner })}
        </p>
      )}
      {/* The card's ONE bar (operator, 2026-10-07): Apply rebuilds — always live,
          a clean card re-applies; Discard drops every draft. */}
      <SaveBar
        dirty={dirty}
        resendable={!blocked}
        blocked={blocked}
        hint={dirty ? 'Rebuilds the WAN in the tasks tray' : 'Rebuilds the WAN from these settings'}
        capability={writeCap}
        pending={pending}
        onSave={applyWan}
        onReset={resetWan}
      />
      {primary && wanPortTarget(primary) && (
        <div className="border-t border-line">
          <WanPorts
            connection={primary}
            base={`/devices/${encodeURIComponent(device.id)}`}
            serial={ipv6Apply?.serial ?? device.serial}
            editable={can(writeCap)}
            href={ipv6Apply?.href}
          />
        </div>
      )}
      {deletable && (
        <div className="border-t border-line">
          <WanDeleteButton
            connection={primary!}
            deviceId={device.id}
            endpoint={deleteEndpoint}
            serial={ipv6Apply?.serial ?? device.serial}
            href={ipv6Apply?.href}
            invalidate={[['device', device.id], ...(ipv6Apply?.invalidate ?? [])]}
          />
        </div>
      )}
    </Card>
  );
}

/**
 * The management connection's card, with the internet WAN card's controls
 * (operator, 2026-10-10: "the mngt wan should have full controls like the
 * internet wan"). The same rows in the same language: how it is managed, the
 * ACS, the firewall, DHCP or static, the IPv4 fields, the VLAN, and ONE bar
 * whose Apply is always live (a clean card re-applies, like the internet
 * card). The write is the header Mgmt dialog's (`management-mode`), straight
 * to the tray; the firewall is the internet card's own verb, so the router
 * has one firewall shown on both cards. No PPPoE, IPv6 or Delete — see
 * `lib/management-card.ts`.
 */
export function ManagementWanCard({
  device,
  connection: c,
  oltId,
  onuId,
  serial,
  mode,
  firewalled,
  href,
  invalidate,
}: {
  device: DeviceDetail;
  connection: WanCandidate;
  oltId: string;
  onuId: string;
  serial: string;
  /** How the ONU is managed now. */
  mode: ManagementMode;
  /** The platform's recorded firewall (`onus.firewalled`), the fallback where the CPE reports none. */
  firewalled: boolean | null;
  href?: string | undefined;
  invalidate: QueryKey[];
}) {
  const { can } = useSession();
  const tasks = useTasks();
  const editable = can('onu.provision');

  const profiles = useQuery({
    queryKey: ['olt', oltId, 'tr069-profiles'],
    queryFn: () =>
      api.get<{ items: Array<{ profileId: string; name: string; isDefault: boolean }> }>(
        `/olts/${encodeURIComponent(oltId)}/tr069-profiles`,
      ),
  });
  const oltVlans = useQuery({
    queryKey: ['olt', oltId, 'vlans'],
    queryFn: () => api.get<Page<OltVlan>>(`/olts/${encodeURIComponent(oltId)}/vlans`),
  });
  const olt = useQuery({
    queryKey: ['olt', oltId],
    queryFn: () => api.get<Olt>(`/olts/${encodeURIComponent(oltId)}`),
  });
  /** A C650 has no static management form; the server's own sentence, from the OLT DTO. */
  const staticRefusal = olt.data?.refusals?.onu_static_mgmt_address ?? null;

  const profileItems = profiles.data?.items ?? [];
  const defaultProfile = (profileItems.find((p) => p.isDefault) ?? profileItems[0])?.profileId ?? '';
  /** Only VLANs the OLT marks for management — this cannot move a router onto a subscriber VLAN. */
  const vlanOptions = useMemo(() => {
    const rows = (oltVlans.data?.items ?? [])
      .filter((v) => v.usedForMgmt)
      .map((v) => ({ value: String(v.vlanId), label: String(v.vlanId), hint: v.description ?? null }));
    if (c.vlanId && !rows.some((r) => r.value === c.vlanId)) {
      rows.push({ value: c.vlanId, label: c.vlanId, hint: 'on the router' });
    }
    return rows.sort((a, b) => Number(a.value) - Number(b.value));
  }, [oltVlans.data, c.vlanId]);

  const firewallDefault = defaultFirewall({ cpe: device.firewall, recorded: firewalled, mode: 'route' });
  /** Where every control OPENS: what the router and the OLT say now. */
  const opened: ManagementCardChoice = {
    mode,
    profileId: defaultProfile,
    vlan: c.vlanId ?? vlanOptions[0]?.value ?? '',
    addressing: c.mode === 'static' ? 'static' : 'dhcp',
    staticIp: c.externalIp ?? '',
    staticMask: c.subnetMask ?? '255.255.255.0',
    staticGateway: c.gateway ?? '',
    firewall: firewallDefault.value,
  };
  const [picks, setPicks] = useState<Partial<ManagementCardChoice>>({});
  const choice: ManagementCardChoice = { ...opened, ...picks };
  const pick = (patch: Partial<ManagementCardChoice>) => setPicks((p) => ({ ...p, ...patch }));
  const dirty = (Object.keys(picks) as Array<keyof ManagementCardChoice>).some((k) => picks[k] !== opened[k]);
  const firewallMoved = picks.firewall !== undefined && picks.firewall !== opened.firewall;
  const blocked = managementApplyBlocked(choice, { staticRefusal, profilesOffered: profileItems.length > 0 });

  const apply = () => {
    if (blocked) return;
    if (!confirm(managementApplyConfirm(choice, { mode }, firewallMoved))) return;
    const sent = { ...choice };
    const firewallToo = firewallMoved;
    setPicks({});
    const base = `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}`;
    tasks.start<{ warnings: number; firewallNote: string | null }>({
      label: `Management — ${serial}`,
      ...(href ? { href } : {}),
      run: async () => {
        const r = await api.post<CliActionResult>(`${base}/management-mode`, managementApplyBody(sent));
        let firewallNote: string | null = null;
        if (firewallToo) {
          const answer = await api.post<{ warnings?: string[] } | null>(`${base}/firewall`, {
            enabled: sent.firewall,
            dryRun: false,
          });
          firewallNote =
            answer?.warnings?.find((w) => /queued|next inform/i.test(w)) ??
            `The firewall is now ${sent.firewall ? 'on' : 'off'}.`;
        }
        return { warnings: r.warnings?.length ?? 0, firewallNote };
      },
      done: (o) => managementDoneText(serial, sent, o),
      invalidate,
    });
  };

  const radio = (on: boolean) => `flex items-center gap-2 text-sm ${on ? 'cursor-pointer text-ink' : 'cursor-not-allowed text-faint'}`;
  return (
    <div>
      <div className="divide-y divide-line">
        <p className="px-5 py-3 text-sm text-muted">This platform's channel to the router (TR-069).</p>
        <div className="grid grid-cols-[9rem_1fr] items-baseline gap-3 px-5 py-2.5">
          <span className="text-sm text-muted">Connection</span>
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-ink">
            <span className="font-medium">{c.label}</span>
            <span className="text-xs text-muted">{c.connectionType}</span>
          </span>
        </div>
        <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-3">
          <label className="pt-0.5 text-sm text-muted">Managed by</label>
          <div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {(['tr069', 'omci'] as const).map((m) => (
                <label key={m} className={radio(editable)}>
                  <input
                    type="radio"
                    name={`mgmt-mode-${c.path}`}
                    checked={choice.mode === m}
                    disabled={!editable}
                    onChange={() => pick({ mode: m })}
                  />
                  {m === 'tr069' ? 'TR-069 (this ACS)' : 'OMCI (the OLT only)'}
                </label>
              ))}
            </div>
            {choice.mode === 'omci' && mode === 'tr069' && (
              <p className="mt-1.5 text-xs text-warn">
                Apply takes the router off TR-069: its WAN, WiFi and LAN are no longer set from this page.
              </p>
            )}
          </div>
        </div>
        {choice.mode === 'tr069' && (
          <div className="grid grid-cols-[9rem_1fr] items-center gap-3 px-5 py-2.5">
            <label className="text-sm text-muted">ACS</label>
            {editable && profileItems.length > 0 ? (
              <SearchSelect
                value={choice.profileId}
                placeholder="Select the ACS"
                options={profileItems.map((p) => ({
                  value: p.profileId,
                  label: p.name,
                  hint: p.isDefault ? "the OLT's default" : null,
                }))}
                onChange={(profileId) => pick({ profileId })}
              />
            ) : (
              <span className="text-sm text-ink">
                {profileItems.find((p) => p.profileId === choice.profileId)?.name ?? (
                  <span className="text-faint">{profiles.isLoading ? 'reading…' : 'none offered for this OLT'}</span>
                )}
              </span>
            )}
          </div>
        )}
        <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-3">
          <label className="pt-0.5 text-sm text-muted">Firewall</label>
          <div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {([true, false] as const).map((on) => (
                <label key={on ? 'on' : 'off'} className={radio(editable)}>
                  <input
                    type="radio"
                    name={`mgmt-firewall-${c.path}`}
                    checked={choice.firewall === on}
                    disabled={!editable}
                    onChange={() => pick({ firewall: on })}
                  />
                  {on ? 'On' : 'Off'}
                </label>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-faint">
              {firewallSourceLabel(firewallDefault, 'route')} · the router's one firewall, the same as on the internet WAN
            </p>
          </div>
        </div>
        <div className="grid grid-cols-[9rem_1fr] items-start gap-3 px-5 py-3">
          <label className="pt-0.5 text-sm text-muted">Addressing</label>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {(['dhcp', 'static'] as const).map((a) => {
              const refused = a === 'static' ? staticRefusal : null;
              const on = editable && !refused;
              return (
                <label key={a} className={radio(on)} title={refused ?? undefined}>
                  <input
                    type="radio"
                    name={`mgmt-addressing-${c.path}`}
                    checked={choice.addressing === a}
                    disabled={!on}
                    onChange={() => pick({ addressing: a })}
                  />
                  {a === 'dhcp' ? 'DHCP' : 'Static'}
                </label>
              );
            })}
          </div>
        </div>
        <ReadRow
          label="WAN MAC"
          value={c.macAddress ? <CopyableValue value={c.macAddress} /> : <span className="text-faint">not reported</span>}
        />
        <ReadRow label="Status" value={c.connected ? 'Connected' : 'Not connected'} />
        {c.connected && c.uptimeSeconds !== null && c.uptimeSeconds > 0 && (
          <ReadRow label="Uptime" value={formatUptime(c.uptimeSeconds)} />
        )}
        <div>
          <div className="px-5 pt-3 text-xs font-medium uppercase tracking-wide text-faint">IPv4</div>
          <div className="divide-y divide-line">
            {choice.addressing === 'static' ? (
              <>
                <EditRow
                  label="IP address"
                  path={null}
                  forceEditable={editable}
                  value={choice.staticIp}
                  onChange={(staticIp) => pick({ staticIp })}
                />
                <MaskRow
                  label="Subnet mask"
                  path={null}
                  forceEditable={editable}
                  value={choice.staticMask}
                  onChange={(staticMask) => pick({ staticMask })}
                />
                <EditRow
                  label="Gateway"
                  path={null}
                  forceEditable={editable}
                  value={choice.staticGateway}
                  onChange={(staticGateway) => pick({ staticGateway })}
                />
              </>
            ) : (
              <>
                <ReadRow label="IP address" value={c.externalIp} mono />
                <ReadRow label="Subnet mask" value={subnetLabel(c.subnetMask ?? null)} mono />
                <ReadRow label="Gateway" value={c.gateway ?? null} mono />
              </>
            )}
            <ReadRow label="DNS" value={c.dns ?? null} mono />
            <ReadRow label="NAT" value={c.natEnabled === null || c.natEnabled === undefined ? null : c.natEnabled ? 'On' : 'Off'} />
          </div>
        </div>
        <div className="grid grid-cols-[9rem_1fr] items-center gap-3 px-5 py-2.5">
          <label className="text-sm text-muted">VLAN</label>
          {editable && vlanOptions.length > 0 ? (
            <SearchSelect
              value={choice.vlan}
              placeholder="Select the management VLAN"
              options={vlanOptions}
              onChange={(vlan) => pick({ vlan })}
            />
          ) : (
            <span className="text-sm text-ink">{c.vlanId ?? '—'}</span>
          )}
        </div>
        <ReadRow
          label="Serves"
          value={c.serves.length > 0 ? c.serves.join(', ') : <span className="text-faint">no socket or SSID bound</span>}
        />
      </div>
      {editable && !blocked && (
        <p className="border-t border-line px-5 py-2.5 text-xs text-warn">
          Set on the OLT; the router reconnects to the ACS. A wrong setting takes it off the platform until it is
          put right from here.
        </p>
      )}
      <SaveBar
        dirty={dirty}
        resendable={!blocked}
        blocked={blocked}
        hint={dirty ? 'Applies management in the tasks tray' : 'Re-applies management from these settings'}
        capability="onu.provision"
        pending={false}
        onSave={apply}
        onReset={() => setPicks({})}
      />
    </div>
  );
}

function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="inline-flex items-center gap-2 font-mono text-xs text-ink hover:text-link"
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      title="Copy"
    >
      {value}
      <Copy className="size-3" />
      {copied && <span className="text-[10px] text-success">copied</span>}
    </button>
  );
}

export function LanCard({
  device,
  onNotice,
  embedded,
}: {
  device: DeviceDetail;
  onNotice: (message: string) => void;
  embedded?: boolean;
}) {
  const apply = useApplyWrites(device.id, onNotice);
  const initial = {
    ip: device.lan.ip ?? '',
    subnetMask: device.lan.subnetMask ?? '',
    dhcpStart: device.lan.dhcpStart ?? '',
    dhcpEnd: device.lan.dhcpEnd ?? '',
    dhcpLeaseTime: device.lan.dhcpLeaseTime ? String(device.lan.dhcpLeaseTime) : '',
    dns: device.lan.dns ?? '',
  };
  // Same guard as the WAN card: a 30s refetch or a post-apply invalidation must
  // not reset the pool the operator is mid-edit on. See useSyncedDraft.
  const { draft: form, baseline, edit: setForm, commit, reset } = useSyncedDraft(initial, [
    device.lan.ip,
    device.lan.subnetMask,
    device.lan.dhcpStart,
    device.lan.dhcpEnd,
    device.lan.dhcpLeaseTime,
    device.lan.dns,
  ]);

  // Diff against the frozen baseline, not the live device — see WanCard / useSyncedDraft.
  const writes: Write[] = [];
  const push = (
    path: string | null | undefined,
    next: string,
    previous: string,
    asNumber = false,
  ) => {
    if (path && next !== previous) writes.push({ path, value: asNumber ? Number(next) : next });
  };
  push(device.lan.writable.ip, form.ip, baseline.ip);
  push(device.lan.writable.subnetMask, form.subnetMask, baseline.subnetMask);
  push(device.lan.writable.dhcpStart, form.dhcpStart, baseline.dhcpStart);
  push(device.lan.writable.dhcpEnd, form.dhcpEnd, baseline.dhcpEnd);
  push(device.lan.writable.dhcpLeaseTime, form.dhcpLeaseTime, baseline.dhcpLeaseTime, true);
  push(device.lan.writable.dns, form.dns, baseline.dns);

  // The full writable set at the current values, for a Re-apply that resends
  // even when nothing changed. Non-empty only, so it never blanks a field.
  const resendWrites: Write[] = [];
  const resend = (path: string | null | undefined, value: string, asNumber = false) => {
    if (path && value) resendWrites.push({ path, value: asNumber ? Number(value) : value });
  };
  resend(device.lan.writable.ip, form.ip);
  resend(device.lan.writable.subnetMask, form.subnetMask);
  resend(device.lan.writable.dhcpStart, form.dhcpStart);
  resend(device.lan.writable.dhcpEnd, form.dhcpEnd);
  resend(device.lan.writable.dhcpLeaseTime, form.dhcpLeaseTime, true);
  resend(device.lan.writable.dns, form.dns);

  const toggleDhcp = useApplyWrites(device.id, onNotice);

  const dhcpToggle =
    device.lan.writable.dhcpEnabled && device.lan.dhcpEnabled !== null ? (
      <Button
        size="sm"
        loading={toggleDhcp.isPending}
        onClick={() =>
          toggleDhcp.mutate([
            { path: device.lan.writable.dhcpEnabled!, value: !device.lan.dhcpEnabled },
          ])
        }
      >
        {device.lan.dhcpEnabled ? 'Disable DHCP' : 'Enable DHCP'}
      </Button>
    ) : null;

  return (
    <Card
      embedded={embedded}
      title={lanCardTitle(device)}
      // Embedded, the enclosing Collapsible owns the header, so the DHCP toggle
      // has no header to sit in — it moves to the first body row below.
      action={embedded ? undefined : dhcpToggle}
    >
      {embedded && dhcpToggle && (
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-2.5">
          <span className="text-sm text-muted">DHCP server</span>
          {dhcpToggle}
        </div>
      )}
      <div className="divide-y divide-line">
        <EditRow
          label="LAN address"
          path={device.lan.writable.ip}
          value={form.ip}
          onChange={(ip) => setForm({ ...form, ip })}
          hint="192.168.1.1"
        />
        <MaskRow
          label="Subnet mask"
          path={device.lan.writable.subnetMask}
          value={form.subnetMask}
          onChange={(subnetMask) => setForm({ ...form, subnetMask })}
        />
        <EditRow
          label="Pool start"
          path={device.lan.writable.dhcpStart}
          value={form.dhcpStart}
          onChange={(dhcpStart) => setForm({ ...form, dhcpStart })}
          hint="192.168.1.2"
        />
        <EditRow
          label="Pool end"
          path={device.lan.writable.dhcpEnd}
          value={form.dhcpEnd}
          onChange={(dhcpEnd) => setForm({ ...form, dhcpEnd })}
          hint="192.168.1.254"
        />
        <EditRow
          label="Lease (seconds)"
          path={device.lan.writable.dhcpLeaseTime}
          value={form.dhcpLeaseTime}
          onChange={(dhcpLeaseTime) => setForm({ ...form, dhcpLeaseTime })}
          type="number"
          hint="86400"
        />
        {/* What the ONU hands its own clients, which is not always its gateway
            and is the first thing to check when a subscriber says the internet
            works but nothing resolves. Both vendors keep it in one field,
            comma-separated. */}
        <EditRow
          label="DNS servers"
          path={device.lan.writable.dns}
          value={form.dns}
          onChange={(dns) => setForm({ ...form, dns })}
          hint="8.8.8.8,1.1.1.1"
        />
      </div>
      <SaveBar
        dirty={writes.length > 0}
        resendable={resendWrites.length > 0}
        pending={apply.isPending}
        onSave={() => apply.mutate(writes.length > 0 ? writes : resendWrites, { onSuccess: commit })}
        onReset={reset}
      />
    </Card>
  );
}

export function WifiCard({
  device,
  onNotice,
  embedded,
}: {
  device: DeviceDetail;
  onNotice: (message: string) => void;
  embedded?: boolean;
}) {
  // Attached VLANs and the wifi port rows feed the per-SSID network mode.
  const attachedQuery = useQuery({
    queryKey: ['device', device.id, 'vlans'],
    queryFn: () => api.get<Page<OnuVlan>>(`/devices/${encodeURIComponent(device.id)}/vlans`),
  });
  const portsQuery = useQuery({
    queryKey: ['device', device.id, 'ports'],
    queryFn: () => api.get<Page<OnuPort>>(`/devices/${encodeURIComponent(device.id)}/ports`),
  });
  const wifiContext = {
    beaconTypes: device.wlans.map((w) => w.securityRaw),
    /**
     * Subscriber VLANs only. The management VLAN is the platform's own path
     * to the ONU — putting a WiFi network on it hands every joined client the
     * route this ONU is managed by, and the picker was offering exactly that.
     */
    vlans: (attachedQuery.data?.items ?? []).filter(
      (v) => v.purpose !== 'management' && v.purpose !== 'voip',
    ),
    ports: portsQuery.data?.items ?? [],
  };

  // Accordion: exactly one SSID open — the one being worked on — with the rest
  // collapsed to a summary line each. The same rule as every stacked panel on
  // the platform (`nextOpenKey`); it spans both band groups, so opening a 5 GHz
  // SSID closes the 2.4 GHz one.
  const [expandedInstance, setExpandedInstance] = useState<string | null>(null);
  const accordion = {
    expanded: expandedInstance,
    toggle: (instance: string) => setExpandedInstance((current) => nextOpenKey(current, instance)),
  };
  if (device.wlans.length === 0) {
    return (
      <Card embedded={embedded} title={wifiCardTitle(device)}>
        <EmptyState
          title="No WLAN instances reported"
          description="Summon the device to refresh its WiFi configuration."
        />
      </Card>
    );
  }

  // Grouped by band, because that is how an operator thinks about them: the
  // 2.4 GHz set and the 5 GHz set, each a run of sequential instances.
  const groups: Array<{ band: Wlan['band']; label: string; wlans: Wlan[] }> = [
    { band: '2.4G', label: '2.4 GHz', wlans: [] },
    { band: '5G', label: '5 GHz', wlans: [] },
    // A WLAN the CPE reports without band evidence must still render — counted
    // in the header, it would otherwise appear in no group and be un-editable.
    { band: 'unknown', label: 'Band not reported', wlans: [] },
  ];
  for (const wlan of device.wlans) {
    const group = groups.find((g) => g.band === wlan.band);
    if (group) group.wlans.push(wlan);
  }
  for (const group of groups) {
    group.wlans.sort((a, b) => Number(a.instance) - Number(b.instance));
  }

  return (
    <Card embedded={embedded} title={wifiCardTitle(device)}>
      <div className="divide-y divide-line">
        {groups
          .filter((group) => group.wlans.length > 0)
          .map((group) => (
            <BandGroup
              key={group.band}
              label={group.label}
              band={group.band}
              wlans={group.wlans}
              allWlans={device.wlans}
              deviceId={device.id}
              wifiContext={wifiContext}
              accordion={accordion}
              onNotice={onNotice}
            />
          ))}
      </div>
    </Card>
  );
}

/**
 * One band's SSIDs. Only the live ones are shown — an ONU reports four
 * instances per radio whether or not they are in use, and four empty rows per
 * band is noise. The next unused instance is one click away.
 */
interface WifiContext {
  vlans: OnuVlan[];
  ports: OnuPort[];
  /**
   * Every auth mode THIS CPE reports, so a write is in the vocabulary it
   * accepts. A ZTE F670L refuses the Huawei spelling of "open" outright
   * (cwmp.9003, live 2026-09-24). See `wlanSecurityWrites`.
   */
  beaconTypes: Array<string | null | undefined>;
}

interface Accordion {
  expanded: string | null;
  toggle: (instance: string) => void;
}

function BandGroup({
  label,
  band,
  wlans,
  allWlans,
  deviceId,
  wifiContext,
  accordion,
  onNotice,
}: {
  label: string;
  band: Wlan['band'];
  wlans: Wlan[];
  allWlans: Wlan[];
  deviceId: string;
  wifiContext: WifiContext;
  accordion: Accordion;
  onNotice: (message: string) => void;
}) {
  const { can } = useSession();
  const apply = useApplyWrites(deviceId, onNotice);
  const queryClient = useQueryClient();

  const live = wlans.filter((w) => w.enabled !== false);
  // The next instance to turn on is the lowest disabled one, so adding follows
  // the hardware's own numbering (2, 3, 4 — then 6, 7, 8 on the other band).
  const next = wlans.find((w) => w.enabled === false);

  /**
   * No dormant slot to switch on — a Huawei ONT reports only the SSIDs that
   * exist, so most of the fleet never had one and the button never showed.
   * The API creates the instance (addObject on the WLAN table), switches it on
   * and hands back the SSID; its editor opens so the operator names it.
   */
  const create = useMutation({
    mutationFn: () =>
      api.post<{ wlan: Wlan; how: 'created' | 'enabled'; message: string }>(
        `/devices/${encodeURIComponent(deviceId)}/wlans`,
        { band },
      ),
    onSuccess: async (result) => {
      onNotice(result.message);
      await queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
      if (accordion.expanded !== result.wlan.instance) accordion.toggle(result.wlan.instance);
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not add the SSID'),
  });

  return (
    <div className="p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm font-medium text-ink">
          {label}
          <Pill tone={band === '5G' ? 'info' : 'neutral'}>{band}</Pill>
          <span className="text-xs font-normal text-faint">
            {live.length} of {wlans.length} in use
          </span>
        </span>

        {can('device.write') &&
          (next && next.enablePath ? (
            <Button
              size="sm"
              icon={<Plus className="size-3.5" />}
              loading={apply.isPending}
              onClick={() => apply.mutate([{ path: next.enablePath!, value: true }])}
              title={`Enable ${next.label}`}
            >
              Add SSID
            </Button>
          ) : (
            <Button
              size="sm"
              icon={<Plus className="size-3.5" />}
              loading={create.isPending}
              onClick={() => create.mutate()}
              title="Create the next SSID on this ONT and name it"
            >
              Add SSID
            </Button>
          ))}
      </div>

      {live.length === 0 ? (
        <p className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm text-faint">
          No SSID is live on this band.
        </p>
      ) : (
        <div className="space-y-2">
          {live.map((wlan) =>
            accordion.expanded === wlan.instance ? (
              <WlanEditor
                key={wlan.instance}
                wlan={wlan}
                mirrorTarget={findMirrorTarget(wlan, allWlans)}
                deviceId={deviceId}
                wifiContext={wifiContext}
                deletable={wlanDeletable(wlan, allWlans, wifiContext.vlans)}
                onCollapse={() => accordion.toggle(wlan.instance)}
                onNotice={onNotice}
              />
            ) : (
              <WlanSummaryRow
                key={wlan.instance}
                wlan={wlan}
                wifiContext={wifiContext}
                onExpand={() => accordion.toggle(wlan.instance)}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

/*
  Which 5 GHz radio a 2.4 GHz one mirrors to is decided by `findMirrorTarget` in
  `@simpleacs/shared` (`wifi-mirror.ts`) — the same rule the phone's WiFi card and
  `POST /api/onus/:id/wifi` (`sameOnBoth`) apply, so "which radio is the pair" has
  one answer on every client. It used to live here.
*/

/**
 * How this SSID joins the network: the ONU's own port row first, then what the
 * CPE says it is bound to.
 *
 * A hotspot applied to a Huawei ONT writes no `wifi_0/<n>` row — the binding is
 * a CPE leaf (`X_HW_LANBIND`) — so 82 units carrying "Mymanga Public WiFi"
 * bridged on VLAN 12 all read "LAN" here (operator, 2026-09-23). The record
 * still wins where it exists: it is what this platform was told to write.
 */
function wifiConnectionOf(wifiContext: WifiContext, wlan: Wlan): string {
  // The OLT's name for the VLAN, where it has one — "Access VLAN 12 · ftth hs".
  const named = (vlanId: number) => {
    const label = wifiContext.vlans.find((v) => v.vlanId === vlanId)?.label;
    return label ? `VLAN ${vlanId} · ${label}` : `VLAN ${vlanId}`;
  };
  const port = wifiContext.ports.find((p) => p.port === `wifi_0/${wlan.instance}`);
  if (port?.mode === 'access' && port.pvid) return `Access ${named(port.pvid)}`;
  const net = wlan.network;
  if (net && net.vlanId !== null && (net.bridged || port === undefined)) {
    return `${net.bridged ? 'Bridged' : 'Access'} ${named(net.vlanId)}`;
  }
  return 'LAN';
}

/**
 * The collapsed line: name, how it joins the network, and its auth — enough to
 * tell the SSIDs apart without opening any of them.
 */
function WlanSummaryRow({
  wlan,
  wifiContext,
  onExpand,
}: {
  wlan: Wlan;
  wifiContext: WifiContext;
  onExpand: () => void;
}) {
  const connection = wifiConnectionOf(wifiContext, wlan);
  return (
    <button
      onClick={onExpand}
      className="flex w-full items-center justify-between gap-3 rounded-lg border border-line bg-surface-2 px-4 py-2.5 text-left transition-colors hover:border-primary/40 hover:bg-surface-3"
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <Wifi className="size-4 shrink-0 text-muted" />
        <span className="truncate text-sm font-medium text-ink">
          {wlan.ssid ?? <span className="text-faint">unnamed</span>}
        </span>
        <span className="hidden text-xs text-faint sm:inline">{wlan.label}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <Pill tone={connection === 'LAN' ? 'neutral' : 'primary'}>{connection}</Pill>
        <Pill state tone={wlan.security === 'open' ? 'warn' : 'info'}>
          {WLAN_SECURITY_LABELS[wlan.security]}
        </Pill>
        <ChevronDown className="size-4 text-faint" />
      </span>
    </button>
  );
}

function WlanEditor({
  wlan,
  mirrorTarget,
  deviceId,
  wifiContext,
  deletable,
  onCollapse,
  onNotice,
}: {
  wlan: Wlan;
  mirrorTarget: Wlan | null;
  deviceId: string;
  wifiContext: WifiContext;
  /** False on the band's own network (WiFi 1, WiFi 5): that one is turned off, never deleted. */
  deletable: boolean;
  onCollapse: () => void;
  onNotice: (message: string) => void;
}) {
  const { can } = useSession();
  const apply = useApplyWrites(deviceId, onNotice);
  const tasks = useTasks();
  /**
   * Two deliberate presses, the consequence said in between — the same shape
   * as deleting an ONU. The work runs in the tray: a Huawei answers a delete
   * in a few seconds, one that has gone quiet takes it at its next inform.
   */
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const startDelete = () => {
    setConfirmingDelete(false);
    onCollapse();
    const name = wlan.ssid ? ` “${wlan.ssid}”` : '';
    tasks.start<{ message: string }>({
      label: `Delete WiFi ${wlan.instance}${name}`,
      run: () =>
        api.delete<{ message: string }>(
          `/devices/${encodeURIComponent(deviceId)}/wlans/${encodeURIComponent(wlan.instance)}`,
        ),
      done: (r) => r.message,
      invalidate: [['device', deviceId]],
    });
  };
  const { draft, baseline, edit, commit } = useSyncedDraft(
    { ssid: wlan.ssid ?? '', passphrase: wlan.passphrase ?? '' },
    [wlan.ssid, wlan.passphrase],
  );
  const { ssid, passphrase } = draft;
  const [reveal, setReveal] = useState(false);
  // Pre-ticked when the two radios already carry the same SSID (or the 2.4 GHz
  // one has no name yet), since that is how a single-SSID install looks and
  // unticking it would be the surprise. The rule is shared with the phone.
  const [mirror, setMirror] = useState(mirrorStartsTicked(wlan, mirrorTarget));

  // Adopt fresh device values only while the card is pristine — the 30s refetch
  // used to snap the pre-filled passphrase back mid-edit, so it read as
  // unchanged and never submitted. `commit` once Apply lands lets the next
  // refetch re-sync to the saved truth. See useSyncedDraft.

  /*
    How this network joins — the subscriber's LAN or a VLAN — is part of THIS
    card's draft and goes with its one Apply (operator, 2026-10-09: "remove the
    save button next to access vlan, the single apply button for the card should
    do all the saving/writing"). Read from the network's own socket record.
  */
  const networkOf = (instance: string): WifiNetworkChoice => {
    const port = wifiContext.ports.find((p) => p.port === `wifi_0/${instance}`);
    return port?.mode === 'access' && port.pvid ? { mode: 'vlan', vlan: String(port.pvid) } : { mode: 'lan', vlan: '' };
  };
  const ownPort = wifiContext.ports.find((p) => p.port === `wifi_0/${wlan.instance}`);
  const network = useSyncedDraft(networkOf(wlan.instance), [ownPort?.mode, ownPort?.pvid]);
  const queryClient = useQueryClient();
  const [applying, setApplying] = useState(false);

  /** '' = leave the auth alone; anything else is an explicit change. */
  const [security, setSecurity] = useState<'' | 'wpa2' | 'open'>('');

  /**
   * The auth writes for one WLAN, from the platform's single definition of
   * them (`wlanSecurityWrites`, in shared) — the bulk SSID action sends the
   * same ones, so a radio cannot end up configured two ways depending on
   * which screen was used. All standard parameters, written through the same
   * per-parameter apply as everything else here, so a CPE that refuses one
   * names it in the result.
   */
  const securityWrites = (target: Wlan, choice: 'wpa2' | 'open'): Write[] =>
    wlanSecurityWrites(target.securityPath, choice, wifiContext.beaconTypes);

  // Diff against the frozen baseline, not the live wlan: an async passphrase
  // fill (from a concurrent resync) must not read as an operator change and get
  // written back — least of all as an empty value. See useSyncedDraft.
  /**
   * Open as it will be once this is applied: chosen now, or already open and
   * left alone. An open network has no key to ask for, so the passphrase field
   * is not shown and no passphrase is sent (operator, 2026-09-24). The key the
   * CPE holds stays where it is, untouched, for if the network is secured again.
   */
  const isOpen = security === 'open' || (security === '' && wlan.security === 'open');

  const writes: Write[] = [];
  const ssidChanged = ssid !== baseline.ssid;
  const passChanged = !isOpen && passphrase !== baseline.passphrase;

  if (wlan.ssidPath && ssidChanged) writes.push({ path: wlan.ssidPath, value: ssid });
  if (wlan.passphrasePath && passChanged) {
    writes.push({ path: wlan.passphrasePath, value: passphrase });
  }
  if (security) writes.push(...securityWrites(wlan, security));

  // Mirroring writes the same values to the paired radio in the same session,
  // so both bands change together rather than drifting apart.
  if (mirror && mirrorTarget) {
    if (mirrorTarget.ssidPath && (ssidChanged || mirrorTarget.ssid !== ssid)) {
      writes.push({ path: mirrorTarget.ssidPath, value: ssid });
    }
    if (
      !isOpen &&
      mirrorTarget.passphrasePath &&
      (passChanged || mirrorTarget.passphrase !== passphrase)
    ) {
      writes.push({ path: mirrorTarget.passphrasePath, value: passphrase });
    }
    if (security) writes.push(...securityWrites(mirrorTarget, security));
  }

  // The current SSID + passphrase, resent even when unchanged — the common
  // "the SSID went but the password never did" case, fixable by re-pushing
  // rather than having to change something first. Passphrase only when it has a
  // value, so a Re-apply never clears a key it does not hold.
  const resendWrites: Write[] = [];
  if (wlan.ssidPath && ssid) resendWrites.push({ path: wlan.ssidPath, value: ssid });
  if (!isOpen && wlan.passphrasePath && passphrase) {
    resendWrites.push({ path: wlan.passphrasePath, value: passphrase });
  }
  if (security) resendWrites.push(...securityWrites(wlan, security));
  if (mirror && mirrorTarget) {
    if (mirrorTarget.ssidPath && ssid) resendWrites.push({ path: mirrorTarget.ssidPath, value: ssid });
    if (!isOpen && mirrorTarget.passphrasePath && passphrase) {
      resendWrites.push({ path: mirrorTarget.passphrasePath, value: passphrase });
    }
    if (security) resendWrites.push(...securityWrites(mirrorTarget, security));
  }

  const plan = planWifiCardApply({
    instance: wlan.instance,
    label: wlan.label,
    enabled: wlan.enabled,
    network: { baseline: network.baseline, draft: network.draft },
    mirror:
      mirror && mirrorTarget
        ? {
            instance: mirrorTarget.instance,
            label: mirrorTarget.label,
            enablePath: mirrorTarget.enablePath,
            enabled: mirrorTarget.enabled,
            network: networkOf(mirrorTarget.instance),
          }
        : null,
    writes,
    resendWrites,
  });
  // The WiFi panel keeps this editor mounted while an edit of the operator's own is pending.
  useUnsavedDraft(plan.edited);

  /**
   * The one Apply: each step in turn, stopping at the first that does not
   * hold — above all a VLAN binding, because a network switched on while bound
   * to nothing joins the subscriber's own LAN. What each step said is gathered
   * into one notice.
   */
  const runApply = async () => {
    setApplying(true);
    const said: string[] = [];
    try {
      for (const step of plan.steps) {
        if (step.kind === 'bind') {
          const r = await api.put<{
            applied: { executed: boolean; errors: string[] } | null;
            router?: string[];
            bound?: boolean | null;
          }>(`/devices/${encodeURIComponent(deviceId)}/ports`, {
            port: `wifi_0/${step.instance}`,
            kind: 'wifi',
            label: step.label,
            mode: 'access',
            pvid: step.vlanId,
            taggedVlans: [],
            enabled: true,
          });
          const router = r.router?.length ? ` ${r.router.join(' ')}` : '';
          // The server decides whether the network is ON its VLAN now (`bound`)
          // — an ONT declining a ZTE-only line is not a failure, and a chassis
          // that took the line is not enough on a router (2026-10-09).
          if (r.bound !== true) {
            const refused = r.applied?.errors.length ? ` The OLT refused: ${r.applied.errors.join(' ')}.` : '';
            throw new Error(`${step.label} was not put on VLAN ${step.vlanId}.${refused}${router} Nothing after it was written.`);
          }
          said.push(`${step.label} on VLAN ${step.vlanId}.${router}`);
        } else if (step.kind === 'detach') {
          const r = await api.post<{ message: string }>(
            `/devices/${encodeURIComponent(deviceId)}/wifi/${encodeURIComponent(step.instance)}/detach`,
            {},
          );
          said.push(r.message);
        } else {
          const r = await api.post<{ delivered: boolean; message: string }>(
            `/devices/${encodeURIComponent(deviceId)}/parameters`,
            { values: step.writes, connectionRequest: true },
          );
          said.push(r.message);
        }
      }
      commit();
      network.commit();
      onNotice(said.join(' '));
    } catch (err) {
      const done = said.length > 0 ? ` Done before it: ${said.join(' ')}` : '';
      onNotice(`${err instanceof Error ? err.message : 'Could not apply'}${done}`);
    } finally {
      setApplying(false);
      await queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    }
  };

  return (
    <div className="rounded-lg border border-primary/30 bg-surface p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          onClick={onCollapse}
          className="flex items-center gap-2 text-sm font-medium text-ink hover:text-link"
          title="Collapse"
        >
          <ChevronUp className="size-4 text-faint" />
          {wlan.label}
          <Pill tone={wlan.band === '5G' ? 'info' : 'neutral'}>{wlan.band}</Pill>
          <Pill state tone={wlan.security === 'open' ? 'warn' : 'info'}>
            {WLAN_SECURITY_LABELS[wlan.security]}
          </Pill>
          {wlan.enabled === false && <Pill tone="warn">disabled</Pill>}
        </button>
        <div className="flex items-center gap-2">
          {can('device.write') && wlan.enablePath && wlan.enabled !== null && (
            <Button
              size="sm"
              onClick={() => apply.mutate([{ path: wlan.enablePath!, value: !wlan.enabled }])}
            >
              {wlan.enabled ? 'Turn off' : 'Turn on'}
            </Button>
          )}
          {can('device.write') && deletable && confirmingDelete && (
            <Button size="sm" onClick={() => setConfirmingDelete(false)}>
              Cancel
            </Button>
          )}
          {can('device.write') && deletable && (
            <Button
              size="sm"
              variant="danger"
              icon={<Trash2 className="size-3.5" />}
              onClick={() => (confirmingDelete ? startDelete() : setConfirmingDelete(true))}
            >
              {confirmingDelete ? 'Confirm delete' : 'Delete'}
            </Button>
          )}
        </div>
      </div>
      {confirmingDelete && (
        <p className="mb-3 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
          {wlan.label}
          {wlan.ssid ? ` “${wlan.ssid}”` : ''} comes off the router and anyone on it drops. A Huawei
          router loses it for good; one with a fixed set of eight switches the slot off. SimpleACS
          forgets its stored name and key too, so nothing puts it back.
        </p>
      )}

      <div className="space-y-3">
        <div>
          <span className="label">SSID</span>
          <Input
            value={ssid}
            onChange={(e) => edit((d) => ({ ...d, ssid: e.target.value }))}
            disabled={!can('device.write') || !wlan.ssidPath}
          />
        </div>
        {!isOpen && (
          <div>
            <span className="label">Passphrase</span>
            <div className="flex gap-2">
              <Input
                type={reveal ? 'text' : 'password'}
                value={passphrase}
                onChange={(e) => edit((d) => ({ ...d, passphrase: e.target.value }))}
                disabled={!can('device.write') || !wlan.passphrasePath}
              />
              <Button
                variant="secondary"
                icon={reveal ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                onClick={() => setReveal((v) => !v)}
              />
            </div>
          </div>
        )}

        {wlan.securityPath && (
          <div>
            <span className="label">Security</span>
            <select
              className="input"
              value={security || wlan.security}
              onChange={(e) => {
                const v = e.target.value;
                setSecurity(v === 'wpa2' || v === 'open' ? v : '');
              }}
              disabled={!can('device.write')}
            >
              {/* The current mode heads the list under its own name; the two
                  offered targets follow. Choosing the current one clears the
                  change — an untouched field is not a write. */}
              {wlan.security !== 'wpa2' && wlan.security !== 'open' && (
                <option value={wlan.security}>
                  {WLAN_SECURITY_LABELS[wlan.security]} (current)
                </option>
              )}
              <option value="wpa2">WPA2 (passphrase)</option>
              <option value="open">Open — no password</option>
            </select>
            {isOpen && (
              <p className="mt-1.5 text-xs text-warn">
                Open means anyone in range can join this network. The stored passphrase stays on
                the CPE but is not asked for.
              </p>
            )}
          </div>
        )}

        <WifiNetworkMode
          instance={wlan.instance}
          wifiContext={wifiContext}
          value={network.draft}
          onChange={(next) => network.edit(next)}
        />

        {mirrorTarget && can('device.write') && (
          <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2">
            <input
              type="checkbox"
              checked={mirror}
              onChange={(e) => setMirror(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs text-muted">
              <span className="flex items-center gap-1.5 text-ink">
                <Link2 className="size-3.5" />
                Also apply to {mirrorTarget.label}
              </span>
              One SSID across both bands, so clients roam instead of picking a radio — the same name,
              security and network, and switched on with this one.
            </span>
          </label>
        )}

        {can('device.write') && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              icon={<Save className="size-3.5" />}
              disabled={plan.steps.length === 0}
              loading={applying}
              onClick={() => void runApply()}
            >
              {plan.changes ? 'Apply' : 'Re-apply'}
              {mirror && mirrorTarget ? ' to both' : ''}
            </Button>
            {!plan.changes && plan.steps.length > 0 && (
              <span className="text-xs text-faint">
                {isOpen
                  ? 'Resends the current SSID, in case it never reached the CPE'
                  : 'Resends the current SSID and passphrase, in case they never reached the CPE'}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * How an SSID joins the network: bridged into the LAN, or onto one of the
 * ONU's attached VLANs — the way a hotspot SSID rides its own VLAN while the
 * home SSID stays on the LAN. A plain control: the choice is part of the WiFi
 * card's draft and goes with the card's one Apply (`planWifiCardApply`), which
 * records it, carries it down the OLT and binds it on the router.
 */
function WifiNetworkMode({
  instance,
  wifiContext,
  value,
  onChange,
}: {
  instance: string;
  wifiContext: WifiContext;
  value: WifiNetworkChoice;
  onChange: (next: WifiNetworkChoice) => void;
}) {
  const { can } = useSession();
  if (!can('device.write')) return null;

  return (
    <div className="rounded-lg border border-line bg-surface-2 px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-xs text-muted">Network</span>
        <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink">
          <input
            type="radio"
            name={`wifi-net-${instance}`}
            checked={value.mode === 'lan'}
            onChange={() => onChange({ mode: 'lan', vlan: '' })}
          />
          LAN
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink">
          <input
            type="radio"
            name={`wifi-net-${instance}`}
            checked={value.mode === 'vlan'}
            disabled={wifiContext.vlans.length === 0}
            onChange={() => onChange({ mode: 'vlan', vlan: String(wifiContext.vlans[0]?.vlanId ?? '') })}
          />
          Access VLAN
        </label>
        {value.mode === 'vlan' && (
          <select
            className="input h-8 w-auto py-1 text-xs"
            value={value.vlan}
            onChange={(e) => onChange({ ...value, vlan: e.target.value })}
          >
            {wifiContext.vlans.map((vlan) => (
              <option key={vlan.id} value={String(vlan.vlanId)}>
                {vlan.vlanId}
                {vlan.label ? ` — ${vlan.label}` : ''}
              </option>
            ))}
          </select>
        )}
      </div>
      {wifiContext.vlans.length === 0 && (
        <p className="mt-1 text-[11px] text-faint">
          Attach VLANs on the VLANs tab to put this SSID on its own VLAN.
        </p>
      )}
    </div>
  );
}

/** A MAC the chassis has learned behind an ONU. See `OnuLearnedMac` on the API. */
export interface LearnedMac {
  mac: string;
  vlanId: number | null;
  ontPort: string | null;
  /** Who made the card, from the IEEE assignment in the first bytes. */
  vendor?: string | null;
  /** The device made the address up, so it belongs to no manufacturer. */
  randomised?: boolean;
}

/**
 * Who is behind the ONU, from whichever side can answer.
 *
 * An ONU on TR-069 answers properly — hostname, address, interface, whether it
 * is still active — because the question is being put to the ONU itself. An
 * OMCI-only ONU cannot be asked at all, and the OLT can only say which MAC
 * addresses it has learned and where. That is a thinner answer and it is
 * labelled as one, but it is the difference between "nobody is connected" and
 * "we have no way of telling", which is what an empty card used to say for most
 * of this fleet.
 */
export function HostsCard({
  device,
  macs = [],
  mode = null,
  action,
}: {
  device?: DeviceDetail | null;
  macs?: LearnedMac[];
  /** The ONU's management mode — it owns which side answers. */
  mode?: 'tr069' | 'omci' | null;
  /** The card's own refresh, supplied by the page that knows which side to ask. */
  action?: ReactNode;
}) {
  const hosts = device?.hosts ?? [];
  /**
   * The mode picks the source, not whichever side happens to have data: a
   * TR-069 ONU is asked itself through the ACS, an OMCI ONU can only be
   * answered by the OLT — and an ONU moved to OMCI often leaves a stale ACS
   * record behind whose old host table must not win over the chassis. An
   * ONU with no recorded mode keeps the old heuristic: the ACS if it has
   * anything, the OLT otherwise.
   */
  const fromOlt = mode === 'omci' || (mode !== 'tr069' && hosts.length === 0);
  const count = fromOlt ? macs.length : hosts.length;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <MonitorSmartphone className="size-4 text-muted" />
          Connected hosts
          <span className="text-xs font-normal text-faint">{count}</span>
          {fromOlt && count > 0 && (
            <Pill tone="neutral">from the OLT — MAC addresses only</Pill>
          )}
        </span>
      }
      action={action}
    >
      {count === 0 ? (
        <EmptyState
          title="No hosts reported"
          description={
            fromOlt
              ? 'The OLT has learned no MAC addresses behind this ONU. Put it on TR-069 for hostnames and addresses.'
              : 'The ONU has not reported a host table, or nothing is connected.'
          }
        />
      ) : fromOlt ? (
        <Table>
          <THead>
            <th className="th">MAC</th>
            <th className="th">Device</th>
            <th className="th">VLAN</th>
            <th className="th">Seen on</th>
          </THead>
          <TBody>
            {macs.map((host, index) => (
              <TRow key={`${host.mac}-${index}`}>
                <td className="td font-mono text-xs">{host.mac}</td>
                {/* The first bytes of a MAC are an IEEE assignment, so this is
                    as close to a hostname as an OMCI-only ONU can get. A phone
                    rotating a private address belongs to nobody, and saying so
                    is better than an empty cell that reads like missing data. */}
                <td className="td">
                  {host.vendor ? (
                    host.vendor
                  ) : host.randomised ? (
                    <span className="text-faint">private address</span>
                  ) : (
                    <span className="text-faint">unknown</span>
                  )}
                </td>
                <td className="td text-muted">{host.vlanId ?? 'untagged'}</td>
                <td className="td text-muted">{host.ontPort ?? '—'}</td>
              </TRow>
            ))}
          </TBody>
        </Table>
      ) : (
        <Table>
          <THead>
            <th className="th">Hostname</th>
            <th className="th">IP address</th>
            <th className="th">MAC</th>
            <th className="th">Interface</th>
            <th className="th">State</th>
          </THead>
          <TBody>
            {hosts.map((host, index) => (
              <TRow key={`${host.mac ?? host.ip ?? index}`}>
                <td className="td">{host.hostname ?? '—'}</td>
                <td className="td font-mono text-xs">{host.ip ?? '—'}</td>
                <td className="td font-mono text-xs text-muted">{host.mac ?? '—'}</td>
                <td className="td text-muted">{host.iface ?? '—'}</td>
                <td className="td">
                  <Pill state tone={host.active ? 'success' : 'neutral'} dot>
                    {host.active ? 'active' : 'idle'}
                  </Pill>
                </td>
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

export function TasksTab({
  deviceId,
  onResync,
}: {
  deviceId: string;
  /**
   * Opens the ONU's own Resync review — passed by the ONU page, to a caller
   * who may resync. The device page has no ONU to resync, so its warnings
   * offer Ignore alone.
   */
  onResync?: () => void;
}) {
  const tasks = useTasks();
  /**
   * Which row is open, by id rather than by object.
   *
   * Both lists refetch on a timer, so holding the row itself would show a
   * reading from whenever it was clicked — on a task that is actively retrying,
   * that is the wrong retry count and the wrong fault.
   */
  const [openTask, setOpenTask] = useState<string | null>(null);
  const [openFault, setOpenFault] = useState<string | null>(null);
  const tasksQuery = useQuery({
    queryKey: ['device', deviceId, 'tasks'],
    queryFn: () => api.get<Page<DeviceTask>>(`/devices/${encodeURIComponent(deviceId)}/tasks`),
    refetchInterval: 10_000,
  });
  const faultsQuery = useQuery({
    queryKey: ['device', deviceId, 'faults'],
    queryFn: () => api.get<Page<DeviceFault>>(`/devices/${encodeURIComponent(deviceId)}/faults`),
    refetchInterval: 15_000,
  });
  /**
   * What the router's own report deserves a look at — not faults the ACS
   * raised, so shown amber, not red (operator, 2026-09-26). Read from the
   * ACS's copy, on the same gentle cadence as the faults.
   */
  const warningsQuery = useQuery({
    queryKey: ['device', deviceId, 'warnings'],
    queryFn: () =>
      api.get<{ items: DeviceWarning[]; ignored: number }>(`/devices/${encodeURIComponent(deviceId)}/warnings`),
    refetchInterval: 30_000,
  });
  const ignoreWarning = (warning: DeviceWarning) => {
    tasks.start({
      label: 'Ignore the multiple-WAN warning',
      run: () =>
        api.post(`/devices/${encodeURIComponent(deviceId)}/warnings/ignore`, {
          kind: warning.kind,
          fingerprint: warning.fingerprint,
        }),
      done: () => 'Ignored while the router reports these same connections — it comes back if one is added or gone.',
      invalidate: [['device', deviceId, 'warnings']],
    });
  };
  const warnings = warningsQuery.data?.items ?? [];

  /**
   * Cancelling a queued ACS task is a write, so it runs in the tasks tray —
   * from the row, or from the detail dialog, which closes first.
   */
  const cancelTask = (task: DeviceTask) => {
    tasks.start({
      label: `Cancel ACS task ${task.name}`,
      run: () =>
        api.delete(`/devices/${encodeURIComponent(deviceId)}/tasks/${encodeURIComponent(task.id)}`),
      done: () => `${task.name} removed from the ACS queue — it will not be delivered to the ONU.`,
      invalidate: [['device', deviceId, 'tasks']],
    });
  };

  return (
    <div className="space-y-5">
      <Card title="Pending tasks">
        {tasksQuery.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : (tasksQuery.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No queued tasks" description="Everything has been delivered." />
        ) : (
          <Table>
            <THead>
              <th className="th">Task</th>
              <th className="th">Queued</th>
              <th className="th">Retries</th>
              <th className="th">Fault</th>
              <th className="th" />
            </THead>
            <TBody>
              {tasksQuery.data?.items.map((task) => (
                /* The row opens the task. Cancel stops the click from
                   travelling up, so pressing it never opens what it deletes. */
                <TRow key={task.id} onClick={() => setOpenTask(task.id)}>
                  <td className="td">
                    {task.name}
                    <span className="ml-2 text-xs text-faint">{taskSummary(task)}</span>
                  </td>
                  <td className="td text-muted">{formatRelative(task.timestamp)}</td>
                  <td className="td tabular-nums">{task.retries ?? 0}</td>
                  <td className="td max-w-xs truncate text-xs text-danger">
                    {task.fault ? (task.fault.message ?? task.fault.code ?? 'refused') : '—'}
                  </td>
                  <td className="td text-right">
                    <Button
                      size="sm"
                      variant="danger"
                      icon={<Trash2 className="size-3.5" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelTask(task);
                      }}
                    >
                      Cancel
                    </Button>
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Card
        title="Faults"
        action={
          <Link to="/onu-types" className="text-xs text-link hover:underline">
            Parameter mapping lives in ONU types
          </Link>
        }
      >
        {warnings.length > 0 && (
          <div className="space-y-3 p-4 pb-0">
            {warnings.map((warning) => (
              <div
                key={`${warning.kind}:${warning.fingerprint}`}
                className="rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm"
              >
                <div className="flex flex-wrap items-start gap-3">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <p className="font-medium text-warn">Warning — more than one routed WAN</p>
                    <p className="text-ink">{warning.summary}</p>
                    <ul className="space-y-0.5 text-xs text-muted">
                      {warning.connections.map((c) => (
                        <li key={c.path} className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-ink">{c.name ?? leafOf(c.path)}</span>
                          <span>{c.vlanId === null ? 'no VLAN reported' : `VLAN ${c.vlanId}`}</span>
                          <Pill state tone={c.status === 'Connected' ? 'success' : 'neutral'} dot>
                            {c.status ?? 'unread'}
                          </Pill>
                          {c.oltMade && <span className="text-faint">made by the OLT</span>}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-muted">{warning.hint}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {onResync && (
                      <Button size="sm" icon={<RotateCw className="size-3.5" />} onClick={onResync}>
                        Resync
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" icon={<EyeOff className="size-3.5" />} onClick={() => ignoreWarning(warning)}>
                      Ignore
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {faultsQuery.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : (faultsQuery.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No faults" />
        ) : (
          <Table>
            <THead>
              <th className="th">Channel</th>
              <th className="th">Code</th>
              <th className="th">Message</th>
              <th className="th">Retries</th>
              <th className="th">When</th>
            </THead>
            <TBody>
              {faultsQuery.data?.items.map((fault) => (
                <TRow key={fault.id} onClick={() => setOpenFault(fault.id)}>
                  <td className="td">{fault.channel}</td>
                  <td className="td font-mono text-xs">{fault.code}</td>
                  <td className="td max-w-md truncate text-danger">{fault.message}</td>
                  <td className="td tabular-nums">{fault.retries}</td>
                  <td className="td text-muted">{formatRelative(fault.timestamp)}</td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <TaskDetailDialog
        task={tasksQuery.data?.items.find((t) => t.id === openTask) ?? null}
        fault={faultsQuery.data?.items.find((f) => f.id === openFault) ?? null}
        onClose={() => {
          setOpenTask(null);
          setOpenFault(null);
        }}
        onCancel={
          openTask
            ? () => {
                const task = tasksQuery.data?.items.find((t) => t.id === openTask);
                setOpenTask(null);
                if (task) cancelTask(task);
              }
            : undefined
        }
      />
    </div>
  );
}

/** What a task will do, in the few words a table cell has room for. */
function taskSummary(task: DeviceTask): string {
  if (task.parameterValues?.length) {
    const [first] = task.parameterValues;
    return task.parameterValues.length === 1
      ? leafOf(first![0])
      : `${leafOf(first![0])} +${task.parameterValues.length - 1} more`;
  }
  if (task.parameterNames?.length) {
    return task.parameterNames.length === 1
      ? leafOf(task.parameterNames[0]!)
      : `${task.parameterNames.length} parameters`;
  }
  // An empty objectName is the root, which is a full refresh rather than nothing.
  if (task.objectName === '') return 'the whole data model';
  return task.objectName ? leafOf(task.objectName) : '';
}

/**
 * The last meaningful part of a TR-069 path.
 *
 * `InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.SSID` is too long for
 * a cell and the useful half is the end. Instance numbers are skipped, since
 * "1" names nothing.
 */
function leafOf(path: string): string {
  const parts = path.split('.').filter((p) => !/^\d+$/.test(p));
  return parts.slice(-2).join('.') || path;
}
